<template>
  <div class="tu-scope page-section tu-pathway-detail" :data-pathway="pathway ? pathwayIconCategory : 'nursing'">
  <div :class="containerClass">
    <div v-if="!pathway && !isLoading" class="pd-empty">
      <div class="pd-empty-inner">
        <tu-breadcrumb
          :items="[
            { label: 'Home', to: { name: 'student-dashboard' } },
            { label: 'Paths', to: { name: 'student-pathways' } },
            { label: 'Not found' }
          ]"
        />

        <h1 class="pd-empty-title">Pathway not found.</h1>
        <p class="pd-empty-sub">This pathway may have been retired. Browse the full catalog instead.</p>
        <router-link :to="{ name: 'student-pathways' }" class="tu-icon-action pd-empty-btn"><tu-destination-icon to="/pathways" /> All paths </router-link>
      </div>
    </div>

    <template v-else>
      <article class="pd-article" aria-labelledby="pd-heading">
        <tu-breadcrumb
          :items="[
            { label: 'Home', to: { name: 'student-dashboard' } },
            { label: 'Paths', to: { name: 'student-pathways' } },
            { label: pathway.title }
          ]"
        />

        <header class="pd-hero pd-hero--band" :style="heroBandStyle">
          <div class="pd-band-main">
          <p v-if="ladderEnds" class="pd-hero-route" aria-label="From and to credential">
            <span class="tu-credential-badge tu-credential-badge--start pd-hero-chip">{{ ladderEnds.start }}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5"/></svg>
            <template v-if="ladderEnds.mid">
              <span class="tu-credential-badge pd-hero-chip">{{ ladderEnds.mid }}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5"/></svg>
            </template>
            <span class="tu-credential-badge pd-hero-chip">{{ ladderEnds.end }}</span>
          </p>
          <span v-if="myEnrollment" class="pd-enrolled-pill"><tu-ui-icon name="verify" :size="16" /> Enrolled</span>
          <h1 id="pd-heading" class="pd-title">{{ heroTitle }}</h1>
          <p v-if="pathway.subtitle" class="pd-subtitle">{{ pathway.subtitle }}</p>

          <p v-if="heroSubtitle" class="pd-lead">{{ heroSubtitle }}</p>
          <dl class="pd-hero-facts">
            <div v-if="pathwayTime(pathway)"><dt>Timeline</dt><dd>{{ pathwayTime(pathway) }}</dd></div>
            <div v-if="pathwayCost(pathway)"><dt>Cost</dt><dd>{{ pathwayCost(pathway) }}</dd></div>
            <div><dt>State</dt><dd>{{ heroState }}</dd></div>
            <div v-if="heroProgramCount"><dt>Programs</dt><dd>{{ $n(heroProgramCount) }}</dd></div>
          </dl>
          <p v-if="heroLanguages.length" class="pd-hero-langs"><span class="pd-langs__flag"><tu-lang-flag :code="(pathway.languages || [])[0]" /></span><span>Support in <b>{{ heroLanguages[0] }}</b></span></p>
          <div class="pd-actions pd-band-actions">
            <a v-if="!myEnrollment" :href="enrollHref" target="_blank" rel="noopener" aria-label="Enroll in Path (opens in a new tab)" class="tu-icon-action pd-start-btn"><tu-destination-icon to="/pathways" />Enroll in Path<svg class="pd-start-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></a>
          </div>
          </div>

          <div class="pd-band-side">
          <!-- Who this path was built with -->
          <a
            v-if="creatorPageUrl"
            class="pd-collab"
            :href="creatorPageUrl"
            target="_blank"
            rel="noopener"
            :aria-label="`See ${recommenderName} on EduNurse`"
          >
            <span class="pd-collab__label">{{ hasPathGuide ? 'Created in collaboration with' : 'Created by' }}</span>
            <img v-if="creatorPhoto" :src="creatorPhoto" alt="" class="pd-collab__av" />
            <span v-else class="pd-collab__av pd-collab__av--person">{{ recommenderInitials }}</span>
            <b class="pd-collab__name">{{ recommenderName }}</b>
            <span class="pd-collab__go">See on EduNurse <svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M200,64V168a8,8,0,0,1-13.66,5.66L140,127.31,69.66,197.66a8,8,0,0,1-11.32-11.32L128.69,116,82.34,69.66A8,8,0,0,1,88,56H192A8,8,0,0,1,200,64Z"/></svg></span>
          </a>
          <span v-else class="pd-hero-photo" aria-hidden="true"><img :src="heroPhoto" alt="" width="960" height="720" /></span>
          <!-- Phones: the action sits under the Created-by card instead of above it -->
          <a v-if="!myEnrollment" :href="enrollHref" target="_blank" rel="noopener" aria-label="Enroll in Path (opens in a new tab)" class="tu-icon-action pd-start-btn pd-start-btn--mobile"><tu-destination-icon to="/pathways" />Enroll in Path<svg class="pd-start-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></a>
          </div>
        </header>

        <tu-section-tabs :items="sectionTabs" />

        <div class="pd-layout">
        <!-- Summary rail: the facts and the one action, always in view -->

        <div class="pd-main">
        <!-- GOOD TO KNOW -->
        <section id="pd-overview" class="pd-panel pd-block pd-know" aria-labelledby="pd-know-heading">
          <div class="pd-ov-grid" :class="{ 'pd-ov-grid--solo': !requirementItems.length || myEnrollment }">
          <div class="pd-ov-main">
          <h2 id="pd-know-heading" class="pd-section-title">{{ myEnrollment ? 'Your progress' : `A clear path to ${toProgramLabel}` }}</h2>
          <p v-if="myEnrollment" class="pd-section-lead">Your four steps on this path, from your first credential to your acceptance. Your prerequisites and programs follow.</p>
          <p v-else class="pd-section-lead">A path gets you ready to apply to {{ toProgramLabel }} programs and helps you submit a strong application. Each program makes its own admission decision.</p>
            <!-- the description: first paragraph, then View more opens the rest in place -->
            <div v-if="!myEnrollment" class="pd-overview-intro-wrap">
              <p v-if="pathIntroduction" class="pd-overview-intro">{{ pathIntroduction }}</p>
              <template v-if="introExpanded"><p v-for="(para, i) in longParagraphs.slice(1)" :key="`intro-${i}`" class="pd-overview-intro">{{ para }}</p></template>
              <button v-if="longParagraphs.length > 1 && !longIsHtml" type="button" class="pd-view-more" @click="introExpanded = !introExpanded">{{ introExpanded ? 'View less' : 'View more' }}</button>
              <div v-if="longIsHtml" class="pd-prose" v-html="pathway.long_description"></div>
            </div>
            <div class="pd-ov-how">
            <h3 v-if="!myEnrollment" class="pd-ov-title">How it works</h3>
            <!-- Enrolled: the same four steps as a tracker, one open circle per step (static for now). -->
            <ul v-if="myEnrollment" class="pd-track" aria-label="Your steps on this path">
              <li v-for="item in pathwaySteps" :key="item.title" class="pd-track__item">
                <span class="pd-track__box" aria-hidden="true"></span>
                <div><h4>{{ item.title }}</h4><p>{{ item.desc }}</p></div>
              </li>
            </ul>
            <ol v-else class="pd-how-list">
            <li v-for="(item, index) in pathwaySteps" :key="item.title">
              <span class="pd-how-num" aria-hidden="true">{{ index + 1 }}</span>
              <div><h4>{{ item.title }}</h4><p>{{ item.desc }}</p></div>
            </li>
          </ol>
            </div>
          </div>
            <!-- Before you start: the requirements set on the path in the admin -->
            <aside v-if="requirementItems.length && !myEnrollment" class="pd-before pd-ov-side" aria-label="Requirements">
              <h3 class="pd-ov-title">Requirements <span class="pd-ov-count">{{ requirementItems.length }}</span></h3>
              <ul v-if="requirementItems.length" class="pd-before__list">
                <li v-for="item in requirementItems" :key="item"><svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg><span>{{ item }}</span></li>
              </ul>
            </aside>
          </div>
        </section>

        <!-- PREREQS: what programs of this kind ask for, grouped; each item opens its details -->
        <section v-if="prereqTotal" id="pd-prereqs" class="pd-panel pd-block pd-prereqs" aria-labelledby="pd-prereqs-heading">
          <h2 id="pd-prereqs-heading" class="pd-section-title pd-prq-head">{{ toProgramLabel }} prerequisites <span class="pd-ov-count">{{ prereqTotal }}</span></h2>
          <p class="pd-section-lead">What {{ toProgramLabel }} programs ask for, from the {{ prereqSummary.offerings.toLocaleString('en-US') }} programs we checked. Every program sets its own list.</p>
          <div class="pd-prq-filter" role="group" aria-label="Filter prerequisites by level">
            <button v-for="f in prqFilters" :key="f.key" type="button" class="pd-prq-seg" :class="{ 'is-on': prqLevel === f.key }" :aria-pressed="prqLevel === f.key ? 'true' : 'false'" @click="prqLevel = f.key">
              <span class="pd-prq-seg__label">{{ f.label }}</span><span class="pd-prq-seg__n">{{ f.n }}</span>
            </button>
          </div>
          <p v-if="!prereqGroups.length" class="pd-prq-none">No {{ (prqFilters.find(f => f.key === prqLevel) || {}).label.toLowerCase() }} prerequisites for {{ toProgramLabel }} programs.</p>
          <details v-for="g in prereqGroups" :key="g.key" class="tu-prq-group" :open="prqLevel !== 'all'">
            <summary class="tu-prq-group__sum"><span class="tu-prq-group__ico" aria-hidden="true"><tu-ui-icon :name="prereqIcon(g.key)" :size="20" /></span><h3 class="tu-prq-group__title">{{ g.label }}</h3><span class="tu-prq-group__count">{{ g.shown.length }}</span><svg class="tu-prq-group__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg></summary>
            <ul class="pd-prq-grid">
              <li v-for="item in g.shown" :key="item.key">
                <button type="button" class="pd-prq-item" :aria-label="`${item.label}: details`" @click="openPrereq(item, g.key)">
                  <span class="pd-prq-item__text"><b>{{ item.label }}</b><small>{{ Math.max(1, Math.round(item.share * 100)) }}% of programs</small></span>
                  <svg class="pd-prq-item__go" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>
                </button>
              </li>
            </ul>
          </details>
        </section>

        <!-- CHECKLIST: programs on this path, each locked until the student starts the path -->
        <section id="pd-programs" class="pd-panel pd-block" aria-labelledby="pd-checklist-heading">
          <div class="pd-prog-head">
            <div>
              <h2 id="pd-checklist-heading" class="pd-section-title pd-prq-head">{{ toProgramLabel }} programs <span class="pd-ov-count">{{ (myEnrollment ? enrolledPrograms.length : totalSchools).toLocaleString('en-US') }}</span></h2>
              <p v-if="myEnrollment" class="pd-section-lead">Starting at <b>{{ get(myEnrollment, 'school.name', 'your school') }}</b>. Each program scores your application; open a scorecard to see what counts and what is still missing.</p>
              <p v-else class="pd-section-lead">This path helps you prepare a strong application. Each program makes its own admission decision.</p>
            </div>
          </div>
          <div v-if="myEnrollment" class="pd-schools-grid pd-enrolled-grid" :class="{ 'is-single': enrolledPrograms.length === 1 }">
            <article v-for="uop in enrolledPrograms" :key="`en-${uop.id}`" class="pd-prog pd-prog--enrolled">
              <div class="pd-prog__head">
                <span v-if="get(uop, 'program.abbr')" class="tu-credential-badge pd-prog__chip">{{ get(uop, 'program.abbr') }}</span>
                <span class="pd-ring" :style="ringStyle(progressFor(uop).percent)" :aria-label="`${progressFor(uop).completed} of ${progressFor(uop).total} steps done`">
                  <span class="pd-ring__inner">{{ progressFor(uop).completed }}/{{ progressFor(uop).total }}</span>
                </span>
              </div>
              <h3 class="pd-prog__title">{{ get(uop, 'program.title', '') }}</h3>
              <div class="pd-prog__offered">
                <span class="pd-prog__offered-label">Offered by</span>
                <div class="pd-prog__offered-row">
                  <span class="pd-prog__logo" aria-hidden="true">
                    <img v-if="get(uop, 'university.small_logo_url')" :src="uop.university.small_logo_url" alt="" />
                    <span v-else>{{ (get(uop, 'university.name', '?') || '?').charAt(0) }}</span>
                  </span>
                  <div class="pd-prog__school-copy">
                    <strong>{{ get(uop, 'university.name', 'University') }}</strong>
                    <p v-if="uniLocation(uop.university)"><tu-ui-icon name="location" :size="13" />{{ uniLocation(uop.university) }}</p>
                  </div>
                </div>
              </div>
              <dl class="pd-prog__rows">
                <div><dt>{{ admitFor(enrolledRow(uop)).label }}</dt><dd>{{ admitFor(enrolledRow(uop)).value }}</dd></div>
                <div v-if="enrolledOffering(uop).prerequisite_count"><dt>Prerequisites</dt><dd>{{ enrolledOffering(uop).prerequisite_count }}</dd></div>
                <div v-if="enrolledOffering(uop).admission_points" class="is-points"><dt>Admission points</dt><dd>+{{ enrolledOffering(uop).admission_points }}</dd></div>
                <div><dt>Your score</dt><dd>{{ $n(progressFor(uop).points) }}</dd></div>
                <div><dt>Steps complete</dt><dd>{{ progressFor(uop).completed }} of {{ progressFor(uop).total }}</dd></div>
              </dl>
              <div class="pd-prog__bar" role="progressbar" :aria-valuenow="progressFor(uop).percent" aria-valuemin="0" aria-valuemax="100" :aria-label="`${progressFor(uop).percent}% of scorecard complete`">
                <span :style="{ width: progressFor(uop).percent + '%' }"></span>
              </div>
              <router-link :to="checklistRoute(uop)" class="pd-prog__cta pd-prog__cta--apply is-live">Apply now<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5"/></svg></router-link>
            </article>
          </div>
          <div v-if="myEnrollment && !enrolledPrograms.length" class="pd-locked">
            <span class="pd-locked-ico" aria-hidden="true"><tu-ui-icon name="school" :size="28" /></span>
            <h3 class="pd-section-title">No program attached yet</h3>
            <p class="pd-section-lead pd-locked-lead">Your school's enrollment portal manages which programs sit on this path.</p>
          </div>
          <div v-if="!myEnrollment && (programEntries.length || programQuery || programState)" class="pd-prog-filters">
            <label class="pd-prog-search"><tu-ui-icon name="search" :size="18" /><input v-model="programQuery" type="search" placeholder="Search by school or city" aria-label="Search programs by school or city" /></label>
            <label class="pd-prog-state"><span>State</span><select v-model="programState" aria-label="Filter programs by state"><option value="">All states</option><option v-for="st in stateOptions" :key="st" :value="st">{{ st }}</option></select><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 9 7 7 7-7"/></svg></label>
            <label class="pd-prog-state pd-prog-sort"><span>Sort</span><select v-model="programSort" aria-label="Sort programs"><option v-for="o in sortOptions" :key="o.value" :value="o.value">{{ o.label }}</option></select><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 9 7 7 7-7"/></svg></label>
          </div>
          <p v-if="!myEnrollment && !shownEntries.length && (programQuery || programState)" class="pd-prog-none">No programs match. Try another school, city or state.</p>
          <div v-if="!myEnrollment && shownEntries.length" class="pd-schools-grid" :class="{ 'is-single': shownEntries.length === 1 }">
            <div
              v-for="row in shownEntries"
              :key="`lk-${row.key}`"
              class="pd-prog pd-prog--locked"
              role="button"
              tabindex="0"
              :aria-label="`Prepare your application for ${row.program.title} at ${row.school.name}`"
              @click="openLock(row)"
              @keydown.enter.prevent="openLock(row)"
              @keydown.space.prevent="openLock(row)"
            >
              <div class="pd-prog__head">
                <span v-if="row.program.abbr" class="tu-credential-badge pd-prog__chip">{{ row.program.abbr }}</span>
                <span class="pd-ring pd-ring--locked" aria-hidden="true"><span class="pd-ring__inner"><tu-ui-icon name="lock" :size="14" /></span></span>
              </div>
              <h3 class="pd-prog__title">{{ row.program.title }}</h3>
              <!-- Offered by, like the program cards -->
              <div class="pd-prog__offered">
                <span class="pd-prog__offered-label">Offered by</span>
                <div class="pd-prog__offered-row">
                  <span class="pd-prog__logo" aria-hidden="true">
                    <img v-if="row.school.logoUrl || row.school.small_logo_url" :src="row.school.logoUrl || row.school.small_logo_url" alt="" />
                    <span v-else>{{ (row.school.name || '?').charAt(0) }}</span>
                  </span>
                  <div class="pd-prog__school-copy">
                    <strong>{{ row.school.name }}</strong>
                    <p v-if="row.school.location || row.school.city"><tu-ui-icon name="location" :size="13" />{{ row.school.location || [row.school.city, row.school.state].filter(Boolean).join(', ') }}</p>
                  </div>
                </div>
              </div>
              <dl class="pd-prog__rows">
                <div><dt>{{ admitFor(row).label }}</dt><dd>{{ admitFor(row).value }}</dd></div>
                <div v-if="row.offering && row.offering.prerequisite_count"><dt>Prerequisites</dt><dd>{{ row.offering.prerequisite_count }}</dd></div>
                <div v-if="row.offering && row.offering.admission_points" class="is-points"><dt>Admission points</dt><dd>+{{ row.offering.admission_points }}</dd></div>
              </dl>
            </div>
          </div>
          <tu-show-more v-if="!myEnrollment && canShowMore" noun="programs" :shown="shownEntries.length" :total="filteredTotal" :loading="loadingMorePrograms" @more="showMorePrograms" />
          <div v-if="!myEnrollment && !programEntries.length && !programQuery && !programState" class="pd-locked">
            <span class="pd-locked-ico" aria-hidden="true"><tu-ui-icon name="lock" :size="28" /></span>
            <h3 class="pd-section-title">Your scorecards unlock when you enroll in this path</h3>
            <p class="pd-section-lead pd-locked-lead">Enroll and a scorecard for each program appears here.</p>
            <a v-if="!myEnrollment" :href="enrollHref" target="_blank" rel="noopener" aria-label="Enroll in Path (opens in a new tab)" class="tu-icon-action pd-start-btn"><tu-destination-icon to="/pathways" />Enroll in Path<svg class="pd-start-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></a>
          </div>
        </section>


        <!-- FAQ + REQUIREMENTS -->
        <section v-if="!myEnrollment" id="pd-faq" class="pd-panel pd-block pd-faq" aria-labelledby="pd-faq-heading">
          <h2 id="pd-faq-heading" class="pd-section-title">Common questions</h2>
          <tu-faq v-if="pathwayFaqs.length" :items="pathwayFaqs" />
        </section>

        <!-- Membership, after the questions -->
        <section v-if="!myEnrollment" class="pd-panel pd-block pd-member" aria-label="EduNurse membership">
          <div class="pd-incl">
            <div class="pd-incl__copy">
              <span class="pd-incl__kicker">Included with this path</span>
              <h3 class="pd-incl__title">Membership, on us.</h3>
              <div class="pd-incl__who">
                <span class="pd-incl__av" aria-hidden="true">
                  <img v-if="creatorPageUrl && creatorPhoto" :src="creatorPhoto" alt="" />
                  <span v-else-if="creatorPageUrl">{{ supportCreatorName.charAt(0).toUpperCase() }}</span>
                  <img v-else :src="brandMark" alt="" class="pd-incl__av-brand" />
                </span>
                <span class="pd-incl__who-text">
                  <b>{{ creatorPageUrl ? `${supportCreatorName}'s community` : 'EduNurse' }}</b>
                  <span v-if="creatorPageUrl">{{ supportCreatorName }} took this path. Enroll and you join their community as a bonus.</span>
                  <span v-else>This path is not tied to an acceptee yet, but you get membership access to our EduNurse members platform.</span>
                </span>
              </div>
              <a v-if="creatorPageUrl" :href="creatorPageUrl" target="_blank" rel="noopener" class="tu-icon-action pd-incl__btn">Meet {{ supportCreatorName }}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5"/></svg></a>
              <a v-if="!creatorPageUrl && !myEnrollment" :href="enrollHref" target="_blank" rel="noopener" class="tu-icon-action pd-incl__btn">Start membership<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5"/></svg></a>
            </div>
            <ul class="pd-incl__list" aria-label="What you get">
              <li><span class="pd-incl__ico"><tu-ui-icon name="chats_circle" :size="24" /></span><div><b>Community</b><span>People on this path and nurses who finished it</span></div></li>
              <li><span class="pd-incl__ico"><tu-ui-icon name="article" :size="24" /></span><div><b>Content</b><span>Posts and guides from people who got in</span></div></li>
              <li><span class="pd-incl__ico"><tu-ui-icon name="gift" :size="24" /></span><div><b>Exclusive products</b><span>Downloads and tools to support you through nursing school and your career</span></div></li>
            </ul>
          </div>
        </section>

        </div>
        </div>


      </article>

      <!-- Locked checklist: what the student unlocks for this program -->
      <tu-prereq-modal v-if="prereqOpen" :item="prereqOpen.item" :group="prereqOpen.group" :program-label="toProgramLabel" :total="prereqSummary ? prereqSummary.offerings : 0" :action-label="myEnrollment ? '' : 'Enroll in Path'" :action-href="myEnrollment ? '' : enrollHref" @close="prereqOpen = null" />
      <div v-if="lockRow" class="pd-modal" @mousedown.self="lockRow = null">
        <div class="pd-modal-card pd-lock-card" role="dialog" aria-modal="true" aria-labelledby="pd-lock-title">
          <button type="button" class="pd-modal-x" aria-label="Close" @click="lockRow = null">
            <svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M208,32H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM181.66,170.34a8,8,0,0,1-11.32,11.32L128,139.31,85.66,181.66a8,8,0,0,1-11.32-11.32L116.69,128,74.34,85.66A8,8,0,0,1,85.66,74.34L128,116.69l42.34-42.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>
          </button>
          <div class="pd-lock-school">
            <span class="pd-lock-logo" aria-hidden="true">
              <img v-if="lockRow.school.small_logo_url || lockRow.school.logoUrl" :src="lockRow.school.small_logo_url || lockRow.school.logoUrl" alt="" />
              <tu-ui-icon v-else name="school" :size="20" />
            </span>
            <span class="pd-lock-school-text">
              <b>{{ lockRow.school.name }}</b>
              <span v-if="lockRow.program.abbr" class="tu-credential-badge pd-lock-chip">{{ lockRow.program.abbr }}</span> {{ lockRow.program.title }}
            </span>
          </div>
          <div class="pd-lock-sign" aria-hidden="true"><tu-ui-icon name="lock" :size="30" /></div>
          <h2 id="pd-lock-title" class="pd-modal-title">Prepare your application to {{ lockRow.program.abbr || lockRow.program.title }}</h2>
          <!-- One scorecard for every program: its checklist steps and points (set from its prerequisites). -->
          <p class="pd-lock-lead">We'll help you figure out which prerequisites you still need and build an application that stands out, with points for every step you complete.</p>
          <p v-if="lockRow.offering && lockRow.offering.admission_points" class="pd-lock-note">{{ lockRow.school.name }} scores applicants on its own rubric, and we map every step to it.</p>
          <p class="pd-lock-total">Enroll in this path and we track it with you.</p>
          <a :href="enrollHref" target="_blank" rel="noopener" aria-label="Enroll in Path (opens in a new tab)" class="tu-icon-action pd-start-btn pd-lock-cta"><tu-destination-icon to="/pathways" />Enroll in Path<svg class="pd-start-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></a>
                  </div>
      </div>

      <!-- How this path was built -->
      <div v-if="aboutOpen" class="pd-modal" @mousedown.self="aboutOpen = false">
        <div class="pd-modal-card" role="dialog" aria-modal="true" aria-labelledby="pd-modal-title">
          <button type="button" class="pd-modal-x" aria-label="Close" @click="aboutOpen = false">
            <svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M208,32H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM181.66,170.34a8,8,0,0,1-11.32,11.32L128,139.31,85.66,181.66a8,8,0,0,1-11.32-11.32L116.69,128,74.34,85.66A8,8,0,0,1,85.66,74.34L128,116.69l42.34-42.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>
          </button>
          <div class="pd-about-head">
            <img v-if="creatorPhoto" :src="creatorPhoto" alt="" class="pd-about-av" />
            <span v-else-if="recommenderInitials" class="pd-about-av pd-about-av--person">{{ recommenderInitials }}</span>
            <span v-else class="pd-about-av pd-about-av--brand" aria-hidden="true"><img :src="brandMark" alt="" /></span>
            <span class="pd-about-kicker">{{ hasPathGuide ? 'Created in collaboration with' : 'Created by' }}</span>
            <h2 id="pd-modal-title" class="pd-modal-title pd-about-name">{{ recommenderName }}</h2>
          </div>
          <h3 class="pd-about-sub">How this path was built</h3>
          <ul v-if="hasPathGuide" class="pd-about-list">
            <li><span class="pd-about-check"><svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg></span><span>This path was created in collaboration with {{ recommenderName }}, who took this route.</span></li>
            <li><span class="pd-about-check"><svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg></span><span>We turned their real journey, the training they finished, the programs they got into and the steps they took, into a path you can follow.</span></li>
            <li><span class="pd-about-check"><svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg></span><span>Program details, tuition, deadlines and pass rates come from the schools in our catalog and are kept current.</span></li>
          </ul>
          <ul v-else class="pd-about-list">
            <li><span class="pd-about-check"><svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg></span><span>This path was built by EduNurse.</span></li>
            <li><span class="pd-about-check"><svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg></span><span>We take the latest admissions requirements, program data and licensure outcomes from the schools in our catalog and turn them into a step-by-step path.</span></li>
            <li><span class="pd-about-check"><svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg></span><span>Every step is there to set you up for success: the credential you start with, the experience programs look for, and what each application needs.</span></li>
          </ul>
          <p class="pd-modal-note">Completing this path does not guarantee admission. Each program sets its own requirements.</p>
          <a :href="`${siteBase}/about`" target="_blank" rel="noopener" class="pd-modal-link">More about EduNurse</a>
        </div>
      </div>
    </template>
    <div v-if="isLoading" class="text-center py-5"><b-spinner /></div>
  </div>
  </div>
</template>
<script>
import axios from 'axios';
import { get } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import TuSectionTabs from '@/components/Ui/TuSectionTabs.vue';
import brandMark from '@/assets/images/logos/edunurse_icon.png';
import TuShowMore from '@/tu/components/ShowMore.vue';
import TuBreadcrumb from '@/tu/components/Breadcrumb.vue';
import TuUiIcon from '@/tu/components/UiIcon.vue';
import TuLangFlag from '@/tu/components/LangFlag.vue';
import TuDestinationIcon from '@/tu/components/DestinationIcon.vue';
import TuFaq from '@/tu/components/Faq.vue';
import TuPrereqModal, { PREREQ_GROUP_ICONS } from '@/tu/components/PrereqModal.vue';
import { programFacts as directoryProgramFacts, programAdmission } from '@/tu/utils/programDirectory';
import { associatedPathwaySchools } from '@/tu/utils/associatedPathwaySchools';
import { PROGRAMS, STATES } from '@/common/programGeo';
import { pathwayCategory, fieldLabel as fieldLabelFor } from '@/tu/utils/pathwayCategory';
import { pathwayHeroPhotoFor } from '@/tu/utils/pathwayImages';
import { pathwayTimeline, pathwayCost, pathwayLanguages, pathwayLanguageNative } from '@/tu/utils/pathwayFacts';
import { stateAbbr } from '@/tu/utils/stateAbbr';
import { ladderStepsFromPathway } from '@/tu/utils/pathwayLadder';

/**
 * Path page, page for page the same as transitionu.health/pathways/<slug>
 * (transition_uni_listings pages/pathways/_slug/index.vue): hero band, Overview, Prereqs,
 * Programs, FAQ, membership. The portal adds one thing: once the student is enrolled, the
 * Programs section shows their scorecards instead of the locked cards.
 */

// The site base for the enroll wizard, about page and creator pages.
const SITE_BASE = String(process.env.VUE_APP_UNI_SPONSORED_URL || 'https://transitionu.health').replace(/\/+$/, '');

const SORT_OPTIONS = [
  { value: '', label: 'Name' }, { value: 'state', label: 'State' },
  { value: 'deadline', label: 'Deadline: soonest' }, { value: '-deadline', label: 'Deadline: latest' },
  { value: 'prereqs', label: 'Prerequisites: fewest' }, { value: '-prereqs', label: 'Prerequisites: most' },
  { value: '-points', label: 'Admission points: highest' }, { value: 'points', label: 'Admission points: lowest' },
];
function sortEntries(entries, sort) {
  const desc = sort.startsWith('-'); const key = sort.replace(/^-/, '');
  const today = new Date().toISOString().slice(0, 10);
  const val = e => {
    const o = e.offering || {};
    if (key === 'state') return (e.school && e.school.state) || null;
    if (key === 'deadline') return o.application_deadline || null;
    if (key === 'prereqs') return o.prerequisite_count || null;
    return o.admission_points || null;
  };
  return [...entries].sort((a, b) => {
    const x = val(a); const y = val(b);
    if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
    if (key === 'deadline' && !desc) { const px = x < today; const py = y < today; if (px !== py) return px ? 1 : -1; }
    const c = x < y ? -1 : x > y ? 1 : 0;
    return desc ? -c : c;
  });
}
function prqItemLevel(item) {
  const req = item.required || 0; const hr = item.highly_recommended || 0; const rec = item.recommended || 0;
  if (req >= hr && req >= rec) return 'required';
  return hr >= rec ? 'highly_recommended' : 'recommended';
}
function destinationCatalog(dest) {
  if (!dest) return null;
  return PROGRAMS.find(p => String(p.id) === String(dest.id) || p.dbSlug === dest.slug || p.slug === dest.slug) ||
    (dest.id ? { id: dest.id, title: dest.title, abbr: dest.abbr, short: dest.abbr, slug: dest.slug, dbSlug: dest.slug } : null);
}
const unwrapList = res => { const p = res && res.data !== undefined ? res.data : res; return p || {}; };

export default {
  name: 'PathwayDetail',
  extends: Page,
  components: { TuLangFlag, TuSectionTabs, TuBreadcrumb, TuFaq, TuUiIcon, TuDestinationIcon, TuShowMore, TuPrereqModal },
  data() {
    return {
      brandMark, siteBase: SITE_BASE, isLoading: true,
      pathway: null, pathwaySchools: [], directoryEntries: [], programTotal: 0, prereqSummary: null,
      myEnrollment: null, progressMap: {}, enrolledOfferings: {},
      geoTotal: 0, introExpanded: false, programsShown: 8, loadingMorePrograms: false, programQuery: '', programState: '',
      filterTimer: null, prereqOpen: null, aboutOpen: false, creatorProfile: null, lockRow: null, prqLevel: 'all', programSort: '',
    };
  },
  computed: {
    heroTitle() { return ((this.pathway && this.pathway.title) || '').replace(/\s*\(Registered Nurse\)\s*$/i, '').trim(); },
    heroSubtitle() { return ((this.pathway && this.pathway.description) || '').trim(); },
    sectionTabs() {
      // Enrolled students see where they are and their programs; the pitch sections are for visitors.
      if (this.myEnrollment) {
        const mine = [{ id: 'pd-overview', label: 'Progress' }];
        if (this.prereqTotal) mine.push({ id: 'pd-prereqs', label: 'Prereqs' });
        mine.push({ id: 'pd-programs', label: 'Programs' });
        return mine;
      }
      const tabs = [{ id: 'pd-overview', label: 'Overview' }];
      if (this.prereqTotal) tabs.push({ id: 'pd-prereqs', label: 'Prereqs' });
      tabs.push({ id: 'pd-programs', label: 'Programs' });
      if (this.pathwayFaqs && this.pathwayFaqs.length) tabs.push({ id: 'pd-faq', label: 'FAQ' });
      return tabs;
    },
    longParagraphs() {
      const raw = (this.pathway && this.pathway.long_description) || '';
      if (!raw.trim()) return [];
      if (/<[a-z][\s\S]*>/i.test(raw)) return [raw];
      return raw.split(/\n\s*\n/).map(t => t.trim()).filter(Boolean);
    },
    longIsHtml() { return /<[a-z][\s\S]*>/i.test((this.pathway && this.pathway.long_description) || ''); },
    pathIntroduction() { return this.longParagraphs.length && !this.longIsHtml ? this.longParagraphs[0] : ''; },
    programEntries() {
      if (!this.pathwaySchools.length) return this.directoryEntries;
      return this.pathwaySchools.map(school => {
        const source = { ...this.pathway.to_program, ...((school.program_offering || {}).program || {}) };
        const catalog = PROGRAMS.find(p => String(p.id) === String(source.id)) || {};
        return { key: String(school.id || school.slug), school, offering: school.program_offering || {}, program: { ...catalog, ...source, dbSlug: source.slug || catalog.dbSlug, icon: this.pathwayIconCategory } };
      });
    },
    enrolledPrograms() { return (get(this.myEnrollment, 'uni_offered_programs', []) || []).filter(uop => uop && uop.university); },
    totalSchools() { return this.programTotal || this.programEntries.length; },
    prereqGroups() {
      const s = this.prereqSummary;
      if (!s || !s.groups) return [];
      const keep = i => this.prqLevel === 'all' || prqItemLevel(i) === this.prqLevel;
      return s.groups.map(g => ({ ...g, shown: g.items.filter(keep) })).filter(g => g.shown.length);
    },
    prereqTotal() { const s = this.prereqSummary; return s && s.groups ? s.groups.reduce((n, g) => n + g.items.length, 0) : 0; },
    prqFilters() {
      const items = ((this.prereqSummary && this.prereqSummary.groups) || []).reduce((all, g) => all.concat(g.items), []);
      const n = key => items.filter(i => prqItemLevel(i) === key).length;
      return [
        { key: 'all', label: 'All', n: items.length },
        { key: 'required', label: 'Required', n: n('required') },
        { key: 'highly_recommended', label: 'Highly recommended', n: n('highly_recommended') },
        { key: 'recommended', label: 'Recommended', n: n('recommended') },
      ].filter(f => f.key === 'all' || f.n);
    },
    stateOptions() { return STATES.map(([, name]) => name).sort(); },
    filteredEntries() {
      if (!this.pathwaySchools.length) return this.programEntries;
      const q = this.programQuery.trim().toLowerCase(); const st = this.programState;
      const kept = this.programEntries.filter(e => {
        const sc = e.school || {};
        if (st && sc.state !== st) return false;
        return !q || [sc.name, sc.city, sc.location].filter(Boolean).join(' ').toLowerCase().includes(q);
      });
      return this.programSort ? sortEntries(kept, this.programSort) : kept;
    },
    sortOptions() { return SORT_OPTIONS; },
    shownEntries() { return this.filteredEntries.slice(0, this.programsShown); },
    filteredTotal() { return this.pathwaySchools.length ? this.filteredEntries.length : Math.max(this.programTotal || 0, this.programEntries.length); },
    canShowMore() {
      if (this.programsShown < this.filteredEntries.length) return true;
      return !this.pathwaySchools.length && this.programEntries.length < this.programTotal;
    },
    toProgramLabel() { return this.programLabel(this.pathway && this.pathway.to_program) || 'target program'; },
    enrollHref() { return `${SITE_BASE}/pathways/${get(this.pathway, 'slug', this.$route.params.id)}/enroll`; },
    pathwayIconCategory() { return this.pathway ? pathwayCategory(this.pathway) : 'nursing'; },
    heroBandStyle() { return {}; },
    heroPhoto() { return (this.pathway && this.pathway.image_url) || pathwayHeroPhotoFor(this.pathway, this.pathwayIconCategory); },
    heroLanguages() { return pathwayLanguages(this.pathway); },
    heroLanguageNative() { return pathwayLanguageNative(this.pathway); },
    heroState() { return stateAbbr(this.pathway && this.pathway.state) || 'All states'; },
    heroProgramCount() { return Number(this.geoTotal || this.programTotal || this.programEntries.length) || 0; },
    ladderSteps() { return this.pathway ? ladderStepsFromPathway(this.pathway) : null; },
    ladderEnds() {
      const steps = this.ladderSteps;
      if (!steps || !steps.length) return null;
      return { start: steps[0].code, mid: steps.length === 3 ? steps[1].code : '', end: steps[steps.length - 1].code };
    },
    supportCreatorName() { const c = this.creatorProfile; return (c && (c.first_name || c.display_name || c.handle)) || this.recommenderName; },
    creatorPageUrl() { const c = this.creatorProfile; return c && c.handle ? `${SITE_BASE}/@${c.handle}` : ''; },
    creatorPhoto() { return (this.creatorProfile && this.creatorProfile.avatar) || ''; },
    hasPathGuide() { const g = this.pathway && this.pathway.advisor; return Boolean(g && (g.name || g.first_name || g.last_name)); },
    recommenderInitials() {
      const a = this.pathway && this.pathway.advisor;
      if (!this.hasPathGuide) return '';
      if (a.initials) return a.initials;
      const f = (a.first_name || '').trim(); const l = (a.last_name || '').trim();
      return ((f[0] || '') + (l[0] || '')).toUpperCase() || String(a.name || '').trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
    },
    recommenderName() {
      const a = this.pathway && this.pathway.advisor;
      if (!a) return 'EduNurse';
      if (a.name) return a.name;
      return [a.first_name, a.last_name].filter(Boolean).join(' ') || 'EduNurse';
    },
    fieldLabel() { return fieldLabelFor(this.pathwayIconCategory); },
    pathwaySteps() {
      const fp = this.pathway.from_program || {};
      const from = this.programLabel(fp) || 'starting';
      const to = this.programLabel(this.pathway.to_program) || 'your';
      const span = fp.week_length_from && fp.week_length_to && fp.duration_type ? `${fp.week_length_from} to ${fp.week_length_to} ${fp.duration_type}` : '';
      const groups = (this.prereqSummary && this.prereqSummary.groups) || [];
      const top = groups.filter(g => g.key === 'college_course' || g.key === 'entrance_exam')
        .reduce((all, g) => all.concat(g.items), []).sort((a, b) => b.share - a.share).slice(0, 3).map(i => i.label);
      const list = top.length > 1 ? `${top.slice(0, -1).join(', ')} and ${top[top.length - 1]}` : top[0];
      const time = this.pathwayTime(this.pathway);
      return [
        { title: `Complete your ${from} program`, desc: `Earn your ${from} credential${span ? ` in ${span}` : ''}. You can start working in patient care right away, earn while you prepare, and bring real patient care experience to your ${to} application.` },
        { title: `Meet the ${to} prerequisites`, desc: list ? `We help you plan and finish what ${to} programs ask for, like ${list}.` : `We help you plan and finish the courses, exams and requirements ${to} programs ask for.` },
        { title: 'Apply with your scorecard', desc: `Each ${to} program you pick gets a scorecard in your portal, so you know what is done and what is next. Each program makes its own admission decision.` },
        { title: `Start your ${to} program`, desc: time ? `Begin your ${to} program with your ${from} experience behind you. Most students finish this path in ${time}.` : `Begin your ${to} program with your ${from} experience behind you.` },
      ];
    },
    requirementItems() {
      const pw = this.pathway || {};
      const prior = (pw.required_prior_programs || []).map(p => `${p.title} credential`);
      const general = (pw.prerequisites || []).map(item => (typeof item === 'string' ? item : item.label));
      return [...prior, ...general];
    },
    pathwayFaqs() { return ((this.pathway && this.pathway.faqs) || []).filter(f => f.question && f.answer).map(f => ({ q: f.question, a: f.answer })); },
  },
  watch: {
    programQuery() { this.scheduleProgramFilter(); },
    programState() { this.scheduleProgramFilter(); },
    programSort() { this.scheduleProgramFilter(); },
  },
  methods: {
    get,
    ...mapActions('pathways', ['getPathway', 'getMyPathwayEnrollments']),
    ...mapActions('student', ['getBulkUniversityChecklistScore']),
    ...mapActions('uni', ['getAllUniversitiesByState', 'getProgramGeoCounts']),
    prereqIcon(group) { return PREREQ_GROUP_ICONS[group] || 'verify'; },
    openPrereq(item, group) { this.prereqOpen = { item, group }; },
    scheduleProgramFilter() {
      this.programsShown = 8;
      if (this.pathwaySchools.length) return;
      clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(() => this.refetchPrograms(), 300);
    },
    async refetchPrograms() {
      this.loadingMorePrograms = true;
      try {
        const res = await this.fetchProgramRows(0);
        if (res) { this.directoryEntries = res.rows; this.programTotal = res.count; }
      } finally { this.loadingMorePrograms = false; }
    },
    async fetchProgramRows(offset) {
      const catalog = destinationCatalog(this.pathway && this.pathway.to_program);
      if (!catalog) return null;
      const params = { program_id: catalog.id, basic: true, ordering: 'name', limit: 24, offset };
      if (this.programQuery.trim()) params.search = this.programQuery.trim();
      if (this.programState) params.state = this.programState;
      else if (this.pathway && this.pathway.state) params.state = this.pathway.state;
      if (this.pathway && (this.pathway.destination_designations || []).length) params.designation_all = this.pathway.destination_designations.join(',');
      if (this.programSort) params.sort = this.programSort;
      try {
        const payload = unwrapList(await this.getAllUniversitiesByState(params));
        const rows = Array.isArray(payload) ? payload : payload.results || [];
        const count = !Array.isArray(payload) && payload.count != null ? Number(payload.count) : rows.length;
        return { count, rows: rows.filter(sc => sc.slug).map(sc => ({ key: `${catalog.id}:${sc.id || sc.slug}`, school: sc, offering: sc.program_offering || {}, program: { ...catalog } })) };
      } catch (e) { return null; }
    },
    async showMorePrograms() {
      const next = this.programsShown + 12;
      const needsFetch = next > this.programEntries.length && !this.pathwaySchools.length && this.programEntries.length < this.programTotal;
      if (needsFetch) {
        this.loadingMorePrograms = true;
        try { await this.fetchNextProgramPage(); } finally { this.loadingMorePrograms = false; }
      }
      this.programsShown = Math.min(next, this.programEntries.length);
    },
    async fetchNextProgramPage() {
      const res = await this.fetchProgramRows(this.directoryEntries.length);
      if (!res) return;
      const seen = new Set(this.directoryEntries.map(e => e.key));
      this.directoryEntries = [...this.directoryEntries, ...res.rows.filter(e => !seen.has(e.key))];
    },
    onModalKey(e) { if (e.key === 'Escape') { this.aboutOpen = false; this.lockRow = null; this.prereqOpen = null; } },
    openLock(row) { this.lockRow = row; },
    listingFacts(row) { return directoryProgramFacts(row); },
    admitFor(row) { return programAdmission((row && row.offering) || {}); },
    programLabel(prog) { return prog ? prog.abbr || prog.title || '' : ''; },
    pathwayTime(p) { return pathwayTimeline(p); },
    pathwayCost(p) { return pathwayCost(p); },
    uniLocation(university) { return [get(university, 'city'), get(university, 'state')].filter(Boolean).join(', '); },
    progressKey(universityId, programId) { return `${Number(universityId)}-${Number(programId)}`; },
    progressFor(uop) {
      const entry = this.progressMap[this.progressKey(get(uop, 'university.id'), get(uop, 'program.id'))] || {};
      const total = Number(entry.total) || 0; const completed = Number(entry.completed) || 0;
      let percent = Number(entry.percent);
      if (!Number.isFinite(percent)) percent = total ? (completed / total) * 100 : 0;
      percent = Math.max(0, Math.min(100, Math.round(percent)));
      return { total, completed, percent, points: Number(entry.points) || 0 };
    },
    enrolledOffering(uop) {
      return this.enrolledOfferings[this.progressKey(get(uop, 'university.id'), get(uop, 'program.id'))] || {};
    },
    enrolledRow(uop) {
      return { offering: this.enrolledOffering(uop) };
    },
    /** The directory row for each enrolled program: deadline, prerequisite count, admission points. */
    async fetchEnrolledOfferings() {
      const map = {};
      await Promise.all(
        this.enrolledPrograms.map(async uop => {
          const universityId = Number(get(uop, 'university.id'));
          const programId = Number(get(uop, 'program.id'));
          if (!universityId || !programId) return;
          try {
            const payload = unwrapList(await this.getAllUniversitiesByState({ program_id: programId, basic: true, limit: 5, search: get(uop, 'university.name', '') }));
            const rows = Array.isArray(payload) ? payload : payload.results || [];
            const row = rows.find(r => Number(r.id) === universityId);
            if (row && row.program_offering) map[this.progressKey(universityId, programId)] = row.program_offering;
          } catch (_err) {
            /* the card shows the score without the admission facts */
          }
        }),
      );
      this.enrolledOfferings = map;
    },
    ringStyle(percent) { return { background: `conic-gradient(var(--tu-blue) ${percent}%, var(--tu-surface-2) 0)` }; },
    // The program scorecard at this university, unlocked by the enrollment.
    checklistRoute(uop) {
      return { name: 'std-cc-uni-checklist-detail', params: { slug: get(uop, 'university.slug') }, query: { scope: 'program', program_id: get(uop, 'program.id') } };
    },
    async fetchProgress() {
      const payload = this.enrolledPrograms
        .map(uop => ({ university_id: Number(get(uop, 'university.id')), program_id: Number(get(uop, 'program.id')) }))
        .filter(p => p.university_id && p.program_id);
      if (!payload.length) { this.progressMap = {}; return; }
      try {
        const response = await this.getBulkUniversityChecklistScore(payload);
        const results = get(response, 'data.data.results') || get(response, 'data.results') || [];
        const map = {};
        results.forEach(item => {
          if (!item) return;
          const progress = item.checklist_progress || {};
          map[this.progressKey(item.university_id, item.program_id)] = { points: item.earned_points_sum, completed: progress.completed_checks, total: progress.total_enabled_checks, percent: progress.completion_percent };
        });
        this.progressMap = map;
      } catch (_err) { this.progressMap = {}; }
    },
    async fetchAll() {
      this.isLoading = true;
      try {
        const id = this.$route.params.id;
        const [pathwayRes, enrollmentsRes] = await Promise.all([this.getPathway(id), this.getMyPathwayEnrollments({ pathway: id, limit: 1 })]);
        this.pathway = get(pathwayRes, 'data', null);
        if (!this.pathway || !this.pathway.id) { this.pathway = null; return; }
        const enrollments = get(enrollmentsRes, 'data.results', []);
        this.myEnrollment = enrollments.find(e => get(e, 'pathway.id') === Number(id)) || null;
        this.pathwaySchools = associatedPathwaySchools(this.pathway);
        if (this.pathway.state && !this.programState) this.programState = this.pathway.state;

        const tasks = [this.fetchProgress(), this.fetchEnrolledOfferings()];
        if (!this.pathwaySchools.length) {
          tasks.push(this.fetchProgramRows(0).then(res => { if (res) { this.directoryEntries = res.rows; this.programTotal = res.count; } }));
        }
        const destId = this.pathway.to_program && this.pathway.to_program.id;
        if (destId) {
          tasks.push(axios.get('api/universities/prerequisites/public-summary/', { params: { program: destId } })
            .then(pr => { const d = pr && pr.data && pr.data.data; this.prereqSummary = d && d.groups && d.groups.length ? d : null; })
            .catch(() => { this.prereqSummary = null; }));
          tasks.push(this.getProgramGeoCounts({ program_id: destId }).then(geo => {
            const d = (geo && geo.data) || {};
            const st = this.pathway.state && (d.states || []).find(r => r.state === this.pathway.state);
            const tagged = (this.pathway.destination_designations || []).length;
            const t = tagged ? 0 : st ? st.count : (this.pathway.state ? 0 : d.total);
            if (t) this.geoTotal = Number(t);
          }).catch(() => {}));
        }
        await Promise.all(tasks);
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    document.addEventListener('keydown', this.onModalKey);
    await this.fetchAll();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onModalKey);
  },
};
</script>

<style lang="scss" scoped>
/* ============================================================
   Pathway detail — modern/simple: flat white, split hero,
   numbered step cards, accent used sparingly.
   Per-pathway accent via --pd-a (text-safe shades).
   ============================================================ */
.tu-pathway-detail {
  --pd-a: var(--tu-blue);
  --pd-a-rgb: 59, 134, 245;
  /* same canvas as the portal */
  background: #fdfdfd;
  color: var(--tu-ink);
  font-family: var(--tu-font-text);
  min-height: 100vh;
  padding-top: var(--tu-fixed-app-bar-height, 76px);
}

/* ---------- 404 ---------- */
.pd-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - var(--tu-fixed-app-bar-height, 76px));

  .pd-empty-inner {
    text-align: center;
    max-width: 480px;
    padding: var(--tu-s-7) var(--tu-container-pad-x);
  }

  .pd-empty-title {
    font-family: var(--tu-font-display);
    font-weight: var(--tu-fw-display);
    font-size: var(--tu-fs-h1);
    letter-spacing: var(--tu-ls-tighter);
    margin: var(--tu-s-3) 0 var(--tu-s-3);
  }

  .pd-empty-sub {
    color: var(--tu-ink-mute);
    margin: 0 0 var(--tu-s-5);
    line-height: var(--tu-lh-body);
  }

  .pd-empty-btn {
    display: inline-flex;
    align-items: center;
    height: 44px;
    padding: 0 22px;
    border-radius: var(--tu-r-md);
    background: var(--tu-cta);
    color: var(--tu-on-cta);
    font-weight: var(--tu-fw-bold);
    font-size: var(--tu-fs-small);
    text-decoration: none;
    transition: background var(--tu-dur-fast) var(--tu-ease);

    &:hover { background: var(--tu-cta-deep); }
  }
}

.pd-empty ::v-deep .tu-bc-list {
  justify-content: center;
}

/* ---------- article shell ---------- */
.pd-article {
  max-width: var(--tu-container);
  margin: 0 auto;
  padding: var(--tu-s-5) var(--tu-container-pad-x) var(--tu-s-9);
}

/* ---------- split hero ---------- */
.pd-hero {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  max-width: 760px;
  margin: 0;
  padding: 20px 0 8px;
}

.pd-hero-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.pd-kicker {
  margin: 0 0 var(--tu-s-3);
  font-family: var(--tu-font-text);
  font-size: var(--tu-fs-kicker);
  font-weight: var(--tu-fw-semi);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--tu-kicker);
}

.pd-title {
  font-family: var(--tu-font-display);
  font-weight: var(--tu-fw-display);
  font-size: clamp(34px, 4.2vw, 52px);
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--tu-ink);
  margin: 0 0 var(--tu-s-4);
  text-wrap: balance;
  scroll-margin-top: calc(env(safe-area-inset-top, 0px) + var(--tu-fixed-app-bar-height, 76px) + var(--tu-s-3));
}

.pd-lead {
  font-size: 18px;
  line-height: 1.65;
  color: var(--tu-ink-body);
  margin: 0 0 var(--tu-s-5);
  max-width: var(--uni-subpage-hero-max);
  text-wrap: pretty;
}

/* checkmark value props (hero + CTA band) */
.pd-checks {
  list-style: none;
  margin: 0 0 var(--tu-s-6);
  padding: 0;
  display: grid;
  gap: 12px;
}

.pd-check {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  font-size: 15px;
  font-weight: var(--tu-fw-medium, 500);
  line-height: 1.45;
  color: var(--tu-ink);
}

.pd-check-icon {
  flex: 0 0 auto;
  width: 21px;
  height: 21px;
  margin-top: 1px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--tu-yellow);
  color: #ffffff;
}

.pd-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--tu-s-4);
}

.tu-scope a.pd-start-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 22px;
  border: 0;
  border-radius: var(--tu-r-md);
  background: var(--tu-cta);
  color: var(--tu-on-cta);
  font-family: var(--tu-font-text);
  font-weight: var(--tu-fw-semi);
  font-size: 14px;
  letter-spacing: 0.02em;
  cursor: pointer;
  text-decoration: none;
  transition: background var(--tu-dur-fast) var(--tu-ease);

  svg { transition: transform var(--tu-dur-fast) var(--tu-ease); }

  &:hover {
    background: var(--tu-cta-deep);
    color: var(--tu-on-cta);

    svg { transform: translateX(3px); }
  }
}

.tu-scope a.pd-view-programs,
.pd-view-programs {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 20px;
  border-radius: var(--tu-r-md);
  background: var(--tu-surface);
  color: var(--tu-blue);
  border: 1px solid var(--tu-blue);
  font-family: var(--tu-font-text);
  font-weight: var(--tu-fw-semi);
  font-size: 14px;
  letter-spacing: 0.02em;
  text-decoration: none;
  transition: border-color var(--tu-dur-fast) var(--tu-ease), background var(--tu-dur-fast) var(--tu-ease);

  svg { transition: transform var(--tu-dur-fast) var(--tu-ease); }

  &:hover {
    background: var(--tu-blue-tint);
    border-color: var(--tu-blue);
    svg { transform: translateY(3px); }
  }
}
.pd-view-chev { flex: none; margin-left: 2px; }

/* ---------- path details and contributor ---------- */
.pd-plate {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 20px;
  padding: 6px 16px 6px 6px;
  background: var(--tu-surface);
  border: 1px solid var(--tu-line);
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(24, 24, 24, .04);
}
.pd-plate-text { font-size: 13px; color: var(--tu-ink-soft); white-space: nowrap; }
.pd-plate { cursor: pointer; text-decoration: none; transition: border-color .15s ease, box-shadow .15s ease, background .15s ease; }
.pd-plate:hover { border-color: var(--tu-line-strong); box-shadow: 0 2px 10px rgba(24, 24, 24, .07); }
.pd-plate:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 3px; }
.pd-plate-av--photo { object-fit: cover; }
.pd-plate-go { width: 14px; height: 14px; flex: none; margin-left: 2px; color: var(--tu-ink-mute); }
.pd-plate:hover .pd-plate-go { color: var(--tu-blue); }

/* How this path was built */
.pd-modal { position: fixed; inset: 0; z-index: 120; display: flex; align-items: center; justify-content: center; padding: 20px; background: rgba(24, 24, 24, .45); backdrop-filter: blur(2px); }
.pd-modal-card { position: relative; width: min(480px, 100%); max-height: 90vh; overflow-y: auto; padding: 28px 28px 26px; background: var(--tu-surface); border-radius: 18px; box-shadow: 0 24px 60px rgba(24, 24, 24, .28); }
.pd-modal-x { position: absolute; top: 14px; right: 14px; display: inline-flex; padding: 7px; border-radius: 9px; color: var(--tu-ink-mute); }
.pd-modal-x:hover { background: var(--tu-surface-2); color: var(--tu-ink); }
.pd-modal-x svg { width: 18px; height: 18px; }
.pd-modal-title { margin: 0 0 16px; font-size: 21px; line-height: 1.25; color: var(--tu-ink); }
.pd-modal-list { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 12px; }
.pd-modal-list li { position: relative; padding-left: 22px; font-size: 14.5px; line-height: 1.6; color: var(--tu-ink-body); }
.pd-modal-list li::before { content: ''; position: absolute; left: 0; top: 9px; width: 7px; height: 7px; border-radius: 50%; background: var(--tu-blue); }
.pd-modal-note { margin: 18px 0 0; padding-top: 16px; border-top: 1px solid var(--tu-line); font-size: 12.5px; line-height: 1.6; color: var(--tu-ink-mute); }
.pd-modal-link { display: inline-flex; align-items: center; margin-top: 14px; font-size: 14px; font-weight: 600; color: var(--tu-blue); text-decoration: none; }
.pd-modal-link:hover { text-decoration: underline; }
@media (max-width: 540px) { .pd-modal-card { padding: 24px 20px 22px; } .pd-plate-text { white-space: normal; } }
.pd-plate-recname { font-weight: 600; color: var(--tu-ink); }

.pd-plate-rec {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
}
.pd-plate-av {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.pd-plate-av--person {
  background: var(--tu-ink);
  color: var(--tu-surface);
  font-family: var(--tu-font-display);
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.02em;
}
.pd-plate-av--brand {
  background: var(--tu-surface);
  box-shadow: inset 0 0 0 1px var(--tu-line);
  justify-content: flex-start;
}
/* crop the wordmark to just the angular mark */
.pd-plate-av--brand img {
  height: 19px;
  width: auto;
  max-width: none;
  margin-left: 7px;
  display: block;
}
.pd-plate-rectext {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.pd-plate-reclabel {
  font-family: var(--tu-font-text);
  font-size: 13px;
  line-height: 1.4;
  font-weight: var(--tu-fw-medium, 500);
  color: var(--tu-ink-soft);
}
.pd-plate-recrole { font-size: 12px; line-height: 1.4; color: var(--tu-ink-soft); }
.pd-plate-recname {
  font-family: var(--tu-font-display);
  font-weight: var(--tu-fw-display);
  font-size: 15px;
  line-height: 1.35;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
  color: var(--tu-ink);
}

.pd-path-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px 24px;
  margin-bottom: var(--tu-s-4);
}

.pd-plate-route {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
}
.pd-plate-arr {
  width: 20px;
  height: 20px;
  color: var(--tu-blue);
  flex: 0 0 auto;
}
.pd-plate-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 0;
}
.pd-plate-stat {
  display: inline-block;
  white-space: nowrap;
  gap: 6px;
  font-family: var(--tu-font-text);
  font-size: 13px;
  font-weight: var(--tu-fw-semi);
  color: var(--tu-ink-soft);
}
.pd-plate-stat svg {
  width: 16px;
  height: 16px;
  color: var(--tu-ink-hint);
  flex: 0 0 auto;
}

/* ---------- prose ---------- */
.pd-prose-intro {
  margin-bottom: var(--tu-s-5);
  padding-bottom: var(--tu-s-4);
  border-bottom: 1px solid var(--tu-line-soft);
}

.pd-prose-heading {
  font-family: var(--tu-font-display);
  font-weight: var(--tu-fw-display);
  font-size: clamp(22px, 2.5vw, 28px);
  letter-spacing: -0.02em;
  color: var(--tu-ink);
  margin: 0 0 var(--tu-s-2);
  line-height: 1.2;
}

.pd-prose-lead {
  margin: 0;
  font-size: var(--tu-fs-small);
  line-height: 1.55;
  color: var(--tu-ink-mute);
  max-width: 52ch;
}

.pd-prose {
  color: var(--tu-ink-soft);
  font-size: 17px;
  line-height: 1.7;
  max-width: 65ch;
  margin-top: var(--tu-s-4);

  ::v-deep {
    > *:first-child {
      margin-top: 0 !important;
    }

    h1, h2, h3, h4 {
      font-family: var(--tu-font-display);
      font-weight: var(--tu-fw-display);
      color: var(--tu-ink);
      letter-spacing: -0.015em;
      line-height: 1.2;
      margin: var(--tu-s-7) 0 var(--tu-s-3);
    }
    h2 { font-size: 26px; }
    h3 { font-size: 20px; }
    h4 { font-size: 17px; }

    p {
      margin: 0 0 var(--tu-s-4);
    }

    ul, ol {
      margin: 0 0 var(--tu-s-4);
      padding-left: 1.4em;
    }

    li {
      margin-bottom: 6px;
      &::marker { color: var(--tu-ink-hint); }
    }

    a {
      color: var(--tu-ink);
      text-decoration: underline;
      text-decoration-color: var(--tu-ink-faint);
      text-underline-offset: 3px;
      transition: text-decoration-color var(--tu-dur-fast) var(--tu-ease);

      &:hover { text-decoration-color: var(--tu-ink); }
    }

    strong { color: var(--tu-ink); font-weight: var(--tu-fw-semi); }
    em { font-style: normal; }

    blockquote {
      border-left: 2px solid var(--tu-ink);
      padding: 4px 0 4px var(--tu-s-4);
      margin: var(--tu-s-5) 0;
      font-family: var(--tu-font-text);
      font-size: 19px;
      line-height: 1.5;
      color: var(--tu-ink);
    }

    hr {
      border: 0;
      border-top: 1px solid var(--tu-line);
      margin: var(--tu-s-7) 0;
    }
  }
}

/* ---------- section rhythm: flat, generous, bordered ---------- */
.pd-section-title {
  font-family: var(--tu-font-display);
  font-size: clamp(22px, 2.2vw, 30px);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--tu-ink);
  margin: 0 0 var(--tu-s-5);
}

.pd-salary, .pd-steps, .pd-schools, .pd-faq {
  padding: 0;
  margin-top: 44px;
  border: 0;
}

/* ---------- payoff ---------- */
.pd-payoff {
  display: grid;
  grid-template-columns: minmax(250px, 0.9fr) 1.4fr;
  gap: 22px;
  align-items: center;
}
@media (max-width: 720px) {
  .pd-payoff { grid-template-columns: 1fr; gap: 18px; }
}
.pd-payoff-hero {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 24px 26px;
  border-radius: 12px;
  background: var(--tu-surface-2);
  border: 1px solid var(--tu-card-border);
  color: #fff;
}
.pd-payoff-plus {
  font-family: var(--tu-font-display);
  /* Sized to fit 5-figure amounts ("+$60,000") inside the card without clipping */
  font-size: clamp(30px, 3.4vw, 42px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: var(--tu-cta);
}
.pd-payoff-unit {
  font-size: 15px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
}
.pd-payoff-sub {
  margin-top: 8px;
  font-size: 12.5px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.78);
}
.pd-payoff-bars { display: grid; gap: 14px; }
.pd-payoff-row {
  display: grid;
  grid-template-columns: minmax(72px, auto) 1fr auto;
  align-items: center;
  gap: 12px;
}
.pd-payoff-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--tu-ink-soft);
  white-space: nowrap;
}
.pd-payoff-track {
  height: 12px;
  border-radius: 999px;
  background: var(--tu-line-soft, rgba(24, 24, 24, 0.07));
  overflow: hidden;
}
.pd-payoff-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}
.pd-payoff-fill--from { background: rgba(24, 24, 24, 0.22); }
.pd-payoff-fill--to { background: var(--tu-cta); }
.pd-payoff-amt {
  font-family: var(--tu-font-display);
  font-size: 15px;
  font-weight: 700;
  color: var(--tu-ink);
  white-space: nowrap;
}
.pd-payoff-amt--to { color: var(--tu-cta); }
.pd-salary-note {
  margin: 14px 0 0;
  font-size: 14.5px;
  color: var(--tu-ink-soft);
}

/* ---------- how it works: horizontal numbered steps ---------- */
.pd-steps-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.pd-step {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: flex-start;
  gap: 16px;
  padding: 22px;
  border: 1px solid var(--tu-card-border);
  border-radius: 14px;
  background: var(--tu-surface);
  box-shadow: var(--tu-shadow-card);
}
.pd-step-num {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--tu-blue);
  color: #fff;
  font-family: var(--tu-font-display);
  font-weight: 700;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pd-step-title { margin: 0 0 8px; font-size: 18px; font-weight: 700; line-height: 1.3; color: var(--tu-ink); }
.pd-step-desc { margin: 0; font-size: 15px; line-height: 1.62; color: var(--tu-ink-soft); }
@media (max-width: 720px) { .pd-steps-list { grid-template-columns: 1fr; } .pd-step { padding: 18px; gap: 12px; } .pd-step-title { min-height: 0; } }

/* ---------- schools ---------- */
/* White cards on a white page have no edge, so the grid sits on a tint.
   Contrast in value rather than hue, same move as the homepage bands. */
.pd-schools {
  background: var(--tu-surface-2);
  border-radius: 18px;
  padding: clamp(22px, 3vw, 34px);
  margin-top: 56px;
}
/* Three across with a tighter gap. Four columns squeezed school names onto
   three lines, which made the cards taller, not more compact. */
.pd-schools-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}
.pd-school-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: var(--tu-surface);
  border: 1px solid var(--tu-line);
  border-radius: 12px;
  padding: 14px 16px;
  text-decoration: none !important;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;
}
.pd-school-logo {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: var(--tu-surface-2, #f5f6f8);
  border: 1px solid var(--tu-line);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.pd-school-logo img {
  width: 72%;
  height: 72%;
  object-fit: contain;
}
.pd-school-initial {
  font-weight: 700;
  font-size: 17px;
  color: var(--tu-ink-mute);
}
.pd-school-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}
.pd-school-card:hover { border-color: var(--tu-line-strong); transform: translateY(-2px); }

/* program-forward hierarchy: the program the student earns leads the card */
.pd-prog-toprow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
}
.pd-prog-tag {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--tu-ink-soft);
  background: var(--tu-surface-2);
  padding: 3px 9px;
  border-radius: 999px;
}
.pd-prog-deadline {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--tu-blue-deep);
  background: var(--tu-blue-tint);
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
}
.pd-prog-deadline svg {
  width: 13px;
  height: 13px;
  flex: 0 0 auto;
}
.pd-prog-facts {
  display: flex;
  gap: 20px;
  margin: 9px 0 3px;
}
.pd-fact { display: flex; flex-direction: column; gap: 1px; }
.pd-fact b {
  font-size: 17px;
  font-weight: 700;
  color: var(--tu-ink);
  letter-spacing: -0.01em;
  line-height: 1.05;
}
.pd-fact i {
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.045em;
  text-transform: uppercase;
  color: var(--tu-ink-hint);
}
.pd-prog-school { font-weight: 700; font-size: 13px; color: var(--tu-ink); letter-spacing: -0.005em; margin-top: 2px; }
.pd-prog-loc { font-size: 11.5px; color: var(--tu-ink-hint); margin-top: 1px; }
.pd-school-cta { margin-top: 8px; font-size: 12.5px; }
/* ---------- FAQ ---------- */
.pd-faq-item {
  border: 1px solid var(--tu-card-border);
  border-radius: 14px;
  padding: 0 20px;
  margin-bottom: 10px;
  background: var(--tu-surface);
}
.pd-faq-q {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 60px;
  cursor: pointer;
  font-weight: 700;
  font-size: 15px;
  color: var(--tu-ink);
  padding: 14px 0;
  list-style: none;
}
.pd-faq-q::-webkit-details-marker { display: none; }
.pd-faq-q::after { content: ''; width: 9px; height: 9px; flex: 0 0 9px; border-right: 2px solid var(--tu-blue); border-bottom: 2px solid var(--tu-blue); transform: rotate(45deg); margin-right: 4px; }
.pd-faq-item[open] .pd-faq-q::after { transform: rotate(225deg); }
.pd-faq-a { margin: 0; padding: 0 0 16px; font-size: 14px; line-height: 1.6; color: var(--tu-ink-soft); }

/* ---------- closing enrollment band ---------- */
.pd-schools-more { display: flex; justify-content: center; margin-top: 28px; }
.pd-schools-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 22px;
  border: 1.5px solid var(--tu-line);
  border-radius: var(--tu-r-md);
  background: var(--tu-surface);
  color: var(--tu-ink);
  font-family: var(--tu-font-text);
  font-weight: var(--tu-fw-semi);
  font-size: 14.5px;
  cursor: pointer;
  transition: border-color var(--tu-dur-fast) ease, background var(--tu-dur-fast) ease;
}
.pd-schools-more-btn:hover { border-color: var(--tu-blue); color: var(--tu-blue-deep, var(--tu-blue)); }

.pd-similar { margin-top: 56px; }
.pd-similar-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;

  @media (max-width: 1024px) { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
  @media (max-width: 720px) { grid-template-columns: 1fr; }
}




/* ---------- responsive ---------- */
@media (max-width: 900px) {
  .pd-hero {
    padding-top: var(--tu-s-3);
    padding-bottom: var(--tu-s-5);
  }

}

@media (max-width: 768px) {
  .pd-article {
    padding: var(--tu-s-4) var(--tu-container-pad-x-md) var(--tu-s-8);
  }
  .pd-prose { font-size: 16px; }
  .pd-actions {
    flex-direction: column;
    align-items: stretch;
    gap: var(--tu-s-3);
    width: 100%;
  }
  .tu-scope a.pd-start-btn,
  .tu-scope a.pd-view-programs {
    width: 100%;
    justify-content: center;
  }
  .tu-scope a.pd-secondary-link {
    text-align: center;
    width: 100%;
  }
}

@media (max-width: 540px) {
  .pd-article {
    padding: var(--tu-s-3) var(--tu-container-pad-x-sm) var(--tu-s-7);
  }
  .pd-lead { font-size: 16.5px; }
}

/* Header attribution stays secondary to the path and its main action. */
.pd-hero.pd-hero--band { display: grid; grid-template-columns: minmax(0, 1fr) 400px; gap: 40px; align-items: center; }
.pd-band-main { min-width: 0; }
.pd-band-side { display: grid; gap: 14px; min-width: 0; }
.pd-hero-photo { display: block; width: 100%; border-radius: 22px; overflow: hidden; background: var(--tu-blue-tint); }
.pd-hero-photo img { display: block; width: 100%; height: auto; aspect-ratio: 4 / 3; object-fit: cover; }
.pd-hero-facts { display: flex; flex-wrap: wrap; gap: 10px 36px; margin: 20px 0 0; padding: 0; }
.pd-hero-facts > div { min-width: 0; }
.pd-hero-facts dt { display: inline-flex; align-items: center; gap: 5px; margin: 0; font-size: 12px; font-weight: 600; letter-spacing: .02em; color: var(--tu-ink-mute); }
.pd-hero-facts dt .tu-ui-icon { color: var(--tu-blue); display: inline-flex; }
.pd-hero-langs { display: flex; align-items: center; margin: 14px 0 0; }
@media (max-width: 760px) { .pd-hero-langs { justify-content: flex-start; } }
.pd-hero-langs { gap: 9px; font-size: 14.5px; color: var(--tu-ink-soft); }
.pd-hero-langs b { font-weight: 700; color: var(--tu-ink); }
.pd-langs__ico { display: inline-grid; place-items: center; width: 28px; height: 28px; border-radius: 8px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); flex: none; }
.pd-langs__ico .tu-ui-icon { display: inline-flex; }
.pd-langs__flag { display: inline-flex; flex: none; }
.pd-hero-facts dd { margin: 2px 0 0; font-family: var(--tu-font-text); font-weight: 700; font-size: 20px; letter-spacing: -0.01em; color: var(--tu-ink); }
.pd-bonus-avatar__brand { width: 50% !important; height: auto !important; object-fit: contain !important; }
.pd-hero-route { display: inline-flex; align-items: center; gap: 8px; margin: -6px 0 14px; }
.pd-hero-route svg { width: 16px; height: 16px; color: var(--tu-ink-mute); }
.pd-hero-route .pd-hero-chip { font-size: 13px; font-weight: 700; padding: 6px 10px; border-radius: 8px; line-height: 1.2; }
.pd-collab {
  display: grid; grid-template-columns: 76px minmax(0, 1fr); align-items: center; text-align: left; gap: 4px 16px;
  min-width: 0; width: 100%; padding: 20px; border-radius: 12px;
  background: var(--tu-surface); border: 1px solid var(--tu-line);
  color: var(--tu-ink); text-decoration: none !important; cursor: pointer; font-family: inherit;
  transition: border-color .15s ease;
}
.pd-collab:hover { border-color: var(--tu-blue); }
.pd-collab:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 3px; }
.pd-collab__label { grid-column: 2; font-size: 12px; font-weight: 500; line-height: 1.4; color: var(--tu-ink-mute); }
.pd-collab__av { grid-column: 1; grid-row: 1 / 4; width: 76px; height: 76px; margin: 0; border-radius: 50%; object-fit: cover; background: var(--tu-surface-2); border: 1px solid var(--tu-line); }
.pd-collab__av--person { display: grid; place-items: center; font-family: var(--tu-font-display); font-size: 26px; font-weight: 700; color: var(--tu-blue-deep); background: var(--tu-blue-tint); }
.pd-collab__av--brand { display: grid; place-items: center; background: #fff; }
.pd-collab__av--brand img { width: 46%; height: auto; }
.pd-collab__name { grid-column: 2; font-family: var(--tu-font-display); font-size: 18px; font-weight: 700; line-height: 1.3; overflow-wrap: anywhere; color: var(--tu-ink); }
.pd-collab__go { grid-column: 2; display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 500; line-height: 1.4; color: var(--tu-blue); }
.pd-collab__go svg { flex: none; width: 13px; height: 13px; }
@media (max-width: 760px) {
  .pd-hero.pd-hero--band { grid-template-columns: 1fr; gap: 20px; }
  .pd-collab { max-width: none; width: 100%; padding: 18px; }
}

/* ---------- Eventbrite-style flow: meta icons, overview, good to know, similar rows ---------- */
.pd-hero .pd-plate-stat { display: inline-flex !important; align-items: center; gap: 6px; }
.pd-hero .pd-plate-stat svg { width: 16px; height: 16px; color: var(--tu-ink-mute); flex: none; }
.pd-more-details { margin-top: 20px; }
.pd-more-details summary { cursor: pointer; color: var(--tu-blue); font-weight: 600; font-size: 14px; }
.pd-more-details .pd-prose { margin-top: 16px; }
.pd-overview__body { position: relative; max-width: 68ch; }
.pd-overview__body p { margin: 0 0 12px; }
.pd-overview__body.is-collapsed { max-height: 9.5em; overflow: hidden; }
.pd-overview__body.is-collapsed::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 3.2em; background: linear-gradient(180deg, rgba(253, 253, 253, 0) 0%, var(--tu-bg) 100%); }
.pd-readmore { margin-top: 10px; padding: 0; border: 0; background: transparent; font-family: var(--tu-font-text); font-size: 14px; font-weight: 600; color: var(--tu-blue); cursor: pointer; }
.pd-readmore:hover { text-decoration: underline; }
.pd-sim-list { display: flex; flex-direction: column; border-top: 1px solid var(--tu-line); }
.pd-sim-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 4px; border-bottom: 1px solid var(--tu-line); text-decoration: none !important; color: var(--tu-ink); transition: background .15s ease; }
.pd-sim-row:hover { background: var(--tu-surface-2); }
.pd-sim-row__main { min-width: 0; }
.pd-sim-row h3 { margin: 0 0 8px; font-family: var(--tu-font-display); font-size: 17px; font-weight: 700; line-height: 1.25; color: var(--tu-ink); }
.pd-sim-row__meta { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 10px; margin: 0; font-family: var(--tu-font-text); font-size: 13px; color: var(--tu-ink-soft); }
.pd-sim-chip { font-size: 11px; padding: 4px 8px; }
.pd-sim-arr { width: 14px; height: 14px; color: var(--tu-ink); }
.pd-sim-row__stat { font-weight: 600; }
.pd-sim-row__stat + .pd-sim-row__stat::before { content: '\00b7'; margin-right: 10px; color: var(--tu-ink-hint); }
.pd-sim-row__go { flex: none; display: grid; place-items: center; width: 32px; height: 32px; border-radius: 50%; background: var(--tu-blue-tint); color: var(--tu-blue); }
.pd-sim-row__go svg { width: 16px; height: 16px; }
.pd-similar { max-width: 760px; }


/* How this path was built: who, then how */
.pd-about-head { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 6px; margin: 4px 0 20px; }
.pd-about-av { width: 80px; height: 80px; border-radius: 50%; object-fit: cover; background: var(--tu-surface-2); border: 3px solid #fff; box-shadow: 0 2px 12px rgba(24, 24, 24, .14); margin-bottom: 6px; }
.pd-about-av--person { display: grid; place-items: center; font-family: var(--tu-font-display); font-size: 26px; font-weight: 700; color: var(--tu-blue-deep); background: var(--tu-blue-tint); }
.pd-about-av--brand { display: grid; place-items: center; background: #fff; border-color: var(--tu-line); }
.pd-about-av--brand img { width: 46%; height: auto; }
.pd-about-kicker { font-family: var(--tu-font-text); font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--tu-ink-mute); }
.pd-about-name { margin: 0 !important; font-size: 22px !important; }
.pd-about-sub { margin: 0 0 12px; font-family: var(--tu-font-display); font-size: 15px; font-weight: 700; color: var(--tu-ink); }
.pd-about-list { list-style: none; margin: 0; padding: 16px 18px; display: flex; flex-direction: column; gap: 12px; border: 1px solid rgba(24, 24, 24, .1); border-radius: 14px; background: var(--tu-surface-2); }
.pd-about-list li { display: flex; align-items: flex-start; gap: 10px; font-family: var(--tu-font-text); font-size: 14.5px; line-height: 1.55; color: var(--tu-ink); }
.pd-about-check { flex: none; display: grid; place-items: center; width: 22px; height: 22px; margin-top: 1px; border-radius: 50%; background: var(--tu-blue); color: #fff; }
.pd-about-check svg { width: 12px; height: 12px; }
</style>

<style scoped>
.pd-steps-note { margin: 18px 0 0; max-width: 760px; color: var(--tu-ink-soft); font-size: 13px; line-height: 1.6; }

.pd-start-chev { width: 18px; height: 18px; flex: none; margin-left: 2px; }

/* ---------- tab view (shared with the student portal pathway page) ---------- */
.pd-tabwrap { display: flex; justify-content: flex-start; margin: 32px 0 28px; }
.pd-tabbar { display: inline-flex; gap: 4px; padding: 4px; border-radius: 12px; background: #fff; border: 1px solid rgba(24, 24, 24, .1); box-shadow: 0 1px 2px rgba(24, 24, 24, .04); overflow-x: auto; scrollbar-width: none; max-width: 100%; }
.pd-tabbar::-webkit-scrollbar { display: none; }
.pd-tabpill { appearance: none; border: 0; background: transparent; display: inline-flex; align-items: center; gap: 8px; flex: 0 0 auto; min-height: 44px; padding: 0 18px; border-radius: 9px; font-family: var(--tu-font-text); font-size: 14px; font-weight: 600; color: var(--tu-ink-soft); white-space: nowrap; cursor: pointer; transition: background .2s ease, color .2s ease, box-shadow .2s ease; }
.pd-tabpill:hover { color: var(--tu-ink); }
.pd-tabpill.is-active { background: var(--tu-blue); color: #fff; box-shadow: var(--tu-shadow-card); }
.pd-panel { animation: pd-fade .28s cubic-bezier(.22, 1, .36, 1); }
.pd-panel.pd-steps, .pd-panel.pd-faq { margin-top: 0; }
@keyframes pd-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .pd-panel { animation: none; } }
@media (max-width: 640px) {
  .pd-tabwrap { margin: 28px 0 22px; }
  .pd-tabbar { display: flex; width: 100%; }
  .pd-tabpill { flex: 1; padding: 0 8px; font-size: 13px; justify-content: center; }
}
.pd-locked { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 10px; max-width: 640px; margin: 0 auto; padding: 44px 24px; border: 1px solid var(--tu-line); border-radius: 14px; background: var(--tu-surface); }
.pd-locked .pd-section-title { margin: 0; }
.pd-locked-ico { display: inline-flex; align-items: center; justify-content: center; width: 52px; height: 52px; border-radius: 50%; background: var(--tu-blue-tint); color: var(--tu-blue-deep); margin-bottom: 4px; }
.pd-locked-lead { margin: 0 0 14px; }
.pd-faq .pd-requirements { margin-top: 24px; }

/* ---------- design system pass: photo band hero, summary rail, content column ---------- */
.pd-hero--band {
  max-width: none;
  margin: 8px 0 0;
  padding: 24px 0 16px;
  border-radius: 0;
  background: transparent;
  color: inherit;
  overflow: visible;
}
.pd-hero.pd-hero--band .pd-title { color: var(--tu-ink); font-size: clamp(30px, 3.6vw, 40px); margin-bottom: 16px; max-width: 24ch; }
.pd-hero.pd-hero--band .pd-lead { color: var(--tu-ink-soft); max-width: 56ch; margin-top: 14px; font-size: 16.5px; }
.pd-hero--band .pd-plate { background: rgba(255, 255, 255, .96); border-color: transparent; }
.pd-hero.pd-hero--band .pd-plate-stat { color: var(--tu-ink-mute); }
.pd-hero--band .pd-plate-arr { color: var(--tu-ink); }
.pd-hero.pd-hero--band .pd-path-meta { margin-top: 0; }
.pd-band-actions { margin-top: 24px; }
.tu-pathway-detail .pd-start-btn.pd-start-btn--mobile { display: none !important; }
@media (max-width: 760px) {
  .pd-band-actions { display: none; }
  .pd-band-side { gap: 18px; }
  .pd-hero-photo { display: block; max-width: none; border-radius: 16px; }
  .pd-hero-photo img { aspect-ratio: 16 / 10; }
  .tu-pathway-detail .pd-start-btn.pd-start-btn--mobile { display: inline-flex !important; width: 100%; justify-content: center; }
}
.pd-rail-note { margin-top: 0; }

.pd-layout { display: block; margin-top: 12px; }
.pd-rail { position: sticky; top: calc(var(--tu-fixed-app-bar-height, 76px) + 20px); }
.pd-rail-card { background: #fff; border: 1px solid rgba(24, 24, 24, .1); border-radius: 14px; padding: 20px; }
.pd-facts { margin: 0 0 18px; display: flex; flex-direction: column; }
.pd-facts > div { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--tu-line-soft); }
.pd-facts > div:last-child { border-bottom: 0; }
.pd-facts dt { margin: 0; font-family: var(--tu-font-text); font-size: 12.5px; font-weight: 600; color: var(--tu-ink-mute); }
.pd-facts dd { margin: 0; font-family: var(--tu-font-text); font-size: 14.5px; font-weight: 700; color: var(--tu-ink); text-align: right; }
.pd-facts-pay dt small { display: block; font-size: 11.5px; font-weight: 500; color: var(--tu-ink-hint); margin-top: 1px; }
.pd-facts-pay dd { font-family: var(--tu-font-display); font-size: 18px; letter-spacing: -0.02em; }
.pd-facts-pay--end dd { font-size: 24px; }
.pd-facts-outcome { flex-direction: column; align-items: stretch !important; }
.pd-facts-outcome dd { display: flex; flex-direction: column; gap: 2px; text-align: left; margin-top: 6px !important; }
.pd-facts-from { font-size: 13px; font-weight: 600; color: var(--tu-ink-mute); }
.pd-facts-to { font-family: var(--tu-font-display); font-size: 22px; font-weight: 700; letter-spacing: -0.02em; color: var(--tu-ink); }
.pd-rail-cta { width: 100%; justify-content: center; }
.pd-rail-note { margin: 12px 0 0; font-size: 12.5px; line-height: 1.5; color: var(--tu-ink-mute); }
.pd-main { min-width: 0; }
.pd-main .pd-block + .pd-block { margin-top: 32px !important; padding-top: 26px !important; border-top: 1px solid var(--tu-line-soft); }
.pd-main .pd-section-lead { margin-bottom: 16px; }
.pd-main .pd-steps-note { margin-top: 12px; }
.pd-main ::v-deep .tu-program-card, .pd-main ::v-deep .tu-faq, .pd-main .pd-requirements, .pd-locked { background: #fff; border-color: rgba(24, 24, 24, .1); box-shadow: none; }
.pd-main .pd-tabwrap { margin: 0 0 22px; }
.pd-main .pd-section-title { font-size: 24px; text-align: left; margin-bottom: 14px; }
.pd-main .pd-steps, .pd-main .pd-faq { background: transparent; padding: 0; border: 0; border-radius: 0; }
.pd-main .pd-steps::before, .pd-main .pd-faq::before { display: none; }
.pd-main .pd-steps .pd-section-title, .pd-main .pd-faq .pd-section-title, .pd-main .pd-steps .pd-section-lead, .pd-main .pd-faq .pd-section-lead { text-align: left; margin-left: 0; margin-right: 0; }
.pd-main .pd-faq, .pd-main .pd-steps { max-width: none; margin-inline: 0; margin-top: 0; }
.pd-main .pd-steps-note { margin-inline: 0; text-align: left; }
.pd-steps ::v-deep .hiw-stepcard { padding: 20px; border-color: var(--tu-line); }
.pd-steps ::v-deep .hiw-stepcard .hiw-icon { width: 40px; height: 40px; border-radius: 10px; }
.pd-steps ::v-deep .hiw-stepcard .hiw-step { background: transparent; color: var(--tu-ink-mute); }
.pd-steps ::v-deep .hiw-stepcard .hiw-heading { margin-top: 14px; font-size: 17px; }
.pd-similar { text-align: left; }
.tu-pathway-detail .pd-similar .pd-section-title, .tu-pathway-detail .pd-similar .pd-section-lead { text-align: left; margin-left: 0; margin-right: 0; }
.pd-similar .pd-section-title, .pd-similar .pd-section-lead { text-align: left; margin-left: 0; }
.pd-main .pd-schools-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
@media (max-width: 1024px) { .pd-main .pd-schools-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.pd-main .pd-steps-list { grid-template-columns: repeat(2, minmax(0, 1fr)); }
@media (max-width: 960px) {
  .pd-layout { grid-template-columns: 1fr; gap: 20px; }
  .pd-rail { position: static; }
  .pd-main .pd-schools-grid, .pd-main .pd-steps-list { grid-template-columns: 1fr; }
}

/* locked program cards in the enrolled-card layout + unlock modal */
.pd-prog { display: flex; flex-direction: column; gap: 12px; padding: 18px; background: var(--tu-surface); border: 1px solid var(--tu-line); border-radius: 12px; font-family: var(--tu-font-text); }
.pd-prog--locked { cursor: pointer; border-style: solid; transition: border-color .16s ease; }
.pd-prog--locked:hover { border-color: var(--tu-blue); }
.pd-prog--locked:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 4px; }
.pd-prog__top { display: flex; align-items: center; gap: 12px; }
.pd-prog__logo { display: grid; place-items: center; flex: 0 0 40px; width: 40px; height: 40px; border-radius: 10px; background: #fff; box-shadow: inset 0 0 0 1px var(--tu-line); overflow: hidden; font-weight: 700; color: var(--tu-ink-mute); }
.pd-prog__logo img { width: 100%; height: 100%; object-fit: contain; }
.pd-prog__copy { min-width: 0; flex: 1; }
.pd-prog__head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.pd-prog__chip { font-size: 11px; padding: 4px 8px; }
.tu-pathway-detail .pd-prog__title { margin: -4px 0 0; font-family: var(--tu-font-text) !important; font-size: 18px; font-weight: 700 !important; letter-spacing: -0.01em; line-height: 1.22; color: var(--tu-ink); }
.pd-prog__offered { display: flex; flex-direction: column; gap: 6px; }
.pd-prog__offered-label { font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--tu-ink-mute); }
.pd-prog__copy b { display: block; font-size: 14px; line-height: 1.35; font-weight: 700; color: var(--tu-ink); }
.pd-prog__copy p { display: flex; align-items: center; gap: 4px; margin: 2px 0 0; font-size: 12.5px; color: var(--tu-ink-mute); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pd-prog__copy p .tu-ui-icon { display: inline-flex; }
.pd-prog__admit { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 12px; border-radius: 10px; background: var(--tu-blue-tint); }
.pd-prog__admit span { font-size: 12.5px; font-weight: 600; color: var(--tu-blue-deep); }
.pd-prog__admit strong { font-size: 14px; font-weight: 700; color: var(--tu-ink); }
.pd-prog__admit--muted { background: var(--tu-surface-2); }
.pd-prog__admit--muted span { color: var(--tu-ink-soft); }
.pd-ring { flex: none; width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; background: var(--tu-surface-2); }
.pd-ring__inner { width: 24px; height: 24px; border-radius: 50%; background: transparent; display: grid; place-items: center; font-size: 11px; font-weight: 700; color: var(--tu-ink-mute); }
.pd-prog__program { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 14px; font-weight: 500; color: var(--tu-ink); }
.pd-prog__next { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 10px; background: var(--tu-blue-tint); font-size: 14px; color: var(--tu-ink); }
.pd-prog__next strong { font-weight: 700; flex: 1; min-width: 0; }
.pd-prog__next-label { font-size: 11px; font-weight: 700; color: var(--tu-blue-deep); text-transform: uppercase; letter-spacing: .06em; }
.pd-prog__next-pts { font-size: 12px; font-weight: 700; color: #1c8a52; }
.pd-prog__next--locked strong { color: var(--tu-ink-soft); }
.pd-prog__facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.pd-prog__facts > div { min-width: 0; }
.pd-prog__facts span { display: block; font-size: 12px; color: var(--tu-ink-soft); }
.pd-prog__facts strong { display: block; margin-top: 3px; font-size: 15px; font-weight: 700; color: var(--tu-ink); }
.pd-prog__facts--dim { display: block; padding-top: 12px; border-top: 1px solid var(--tu-line-soft); }
.pd-prog__facts--dim > div { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.pd-prog__facts--dim strong { margin: 0; font-size: 13px; font-weight: 600; color: var(--tu-ink-soft); }
.pd-prog__actions { display: flex; align-items: center; gap: 14px; margin-top: 2px; }
.pd-prog__btn { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 40px; padding: 7px 14px; border-radius: var(--tu-r-sm); background: var(--tu-ink); color: #fff; font-size: 14px; font-weight: 700; white-space: nowrap; transition: background .15s ease; }
.pd-prog--locked:hover .pd-prog__btn { background: var(--tu-blue); }
.pd-prog__link { font-size: 14px; font-weight: 700; color: var(--tu-blue) !important; text-decoration: none !important; white-space: nowrap; }

/* (old preview-pill card) */
.pd-locked-card { position: relative; cursor: pointer; border-radius: 14px; }
.pd-locked-card:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 4px; }
.pd-locked-card ::v-deep .tu-program-card { pointer-events: none; border-style: solid; }
.pd-locked-card ::v-deep .tpc-admission, .pd-locked-card ::v-deep .tpc-details, .pd-locked-card ::v-deep .tpc-facts { opacity: .6; }
.pd-locked-card ::v-deep .tu-card-action { visibility: hidden; }
.pd-locked-badge { position: absolute; left: 20px; bottom: 20px; display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 999px; background: var(--tu-ink); color: #fff; font-family: var(--tu-font-text); font-size: 12.5px; font-weight: 700; pointer-events: none; transition: background .15s ease; }
.pd-locked-card:hover .pd-locked-badge { background: var(--tu-blue); }

/* How it works as a rail, not four boxes */
.pd-rail-steps { list-style: none; margin: 0; padding: 0; max-width: 640px; }
.pd-rail-step { position: relative; display: grid; grid-template-columns: 36px minmax(0, 1fr); gap: 16px; padding: 0 0 26px; }
.pd-rail-step:not(:last-child)::before { content: ''; position: absolute; left: 17px; top: 40px; bottom: 0; width: 2px; background: var(--tu-line); }
.pd-rail-step:last-child { padding-bottom: 0; }
.pd-rail-step-body { padding-top: 6px; }
.pd-rail-step .pd-step-title { margin: 0 0 4px; font-size: 17px; }
.pd-rail-step .pd-step-desc { font-size: 14.5px; }
.pd-lock-card { width: min(520px, 100%); }
.pd-lock-school { display: flex; align-items: center; gap: 12px; margin: 0 36px 16px 0; }
.pd-lock-logo { display: grid; place-items: center; flex: 0 0 44px; width: 44px; height: 44px; border-radius: 10px; background: var(--tu-surface-2); overflow: hidden; color: var(--tu-blue); }
.pd-lock-logo img { width: 100%; height: 100%; object-fit: contain; }
.pd-lock-school-text { display: block; min-width: 0; font-size: 14px; line-height: 1.5; color: var(--tu-ink-soft); }
.pd-lock-school-text b { display: block; margin-bottom: 4px; font-size: 15px; color: var(--tu-ink); }
.pd-lock-school-text .pd-lock-chip { display: inline-flex; vertical-align: middle; }
.pd-lock-chip { font-size: 11px; padding: 3px 7px; margin-right: 6px; }
.pd-lock-sign { display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px; border-radius: 50%; background: var(--tu-blue-tint); color: var(--tu-blue-deep); margin: 4px 0 16px; }
.pd-lock-kicker { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 999px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); font-family: var(--tu-font-text); font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 12px; }
.pd-lock-card .pd-modal-title { margin-bottom: 8px; }
.pd-lock-lead { margin: 0 0 16px; font-size: 14.5px; line-height: 1.6; color: var(--tu-ink-soft); }
.pd-lock-steps { list-style: none; margin: 0; padding: 0; border: 1px solid var(--tu-line); border-radius: 12px; overflow: hidden; }
.pd-lock-steps li { display: flex; align-items: center; gap: 10px; padding: 10px 14px; font-family: var(--tu-font-text); font-size: 14px; font-weight: 600; color: var(--tu-ink); border-bottom: 1px solid var(--tu-line-soft); }
.pd-lock-steps li:last-child { border-bottom: 0; }
.pd-lock-steps li.is-faded { opacity: .45; }
.pd-lock-steps li > .tu-ui-icon { color: var(--tu-ink-hint); flex: none; }
.pd-lock-num { flex: none; width: 24px; height: 24px; border-radius: 50%; background: var(--tu-blue-tint); color: var(--tu-blue-deep); font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
.pd-lock-text { flex: 1; min-width: 0; }
.pd-lock-pts { flex: none; font-size: 12px; font-weight: 700; color: #1c8a52; }
.pd-prog__rows .is-points dd { color: #1c8a52; font-weight: 800; }
.pd-prq-filter { display: flex; flex-wrap: wrap; gap: 8px; margin: 18px 0 6px; }
.pd-prq-seg { display: inline-flex; align-items: center; gap: 10px; min-height: 40px; padding: 0 8px 0 16px; border: 1px solid var(--tu-line); border-radius: 999px; background: var(--tu-surface); color: var(--tu-ink); font: 600 14.5px var(--tu-font-text); white-space: nowrap; cursor: pointer; transition: border-color .15s ease, background .15s ease, color .15s ease; }
.pd-prq-seg__n { display: inline-grid; place-items: center; min-width: 26px; height: 26px; padding: 0 8px; border-radius: 999px; background: var(--tu-surface-2, #f3f5f8); font-size: 12.5px; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--tu-ink-soft); }
.pd-prq-seg:hover { border-color: var(--tu-line-strong); }
.pd-prq-seg.is-on { border-color: var(--tu-blue); background: var(--tu-blue-tint); color: var(--tu-blue-deep); }
.pd-prq-seg.is-on .pd-prq-seg__n { background: var(--tu-blue); color: #fff; }
.pd-prq-seg:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 2px; }
.pd-prq-none { margin: 12px 0 0; font-size: 14.5px; color: var(--tu-ink-soft); }
.pd-prq-dot { width: 9px; height: 9px; border-radius: 50%; flex: none; }
.pd-prq-dot.is-required { background: var(--tu-ink); }
.pd-prq-dot.is-highly_recommended { background: var(--tu-blue); }
.pd-prq-dot.is-recommended { background: #9dc0fb; }
.pd-subtitle { margin: -6px 0 14px; font-family: var(--tu-font-text); font-size: 18px; line-height: 1.4; color: var(--tu-ink-soft); }
.pd-hero-state { margin-left: 6px; padding: 4px 10px; border-radius: 999px; background: var(--tu-surface-2, #f3f5f8); color: var(--tu-ink-soft); font-size: 12.5px; font-weight: 700; }
.pd-lock-note { margin: -6px 0 14px; font-size: 13px; color: var(--tu-ink-mute); }
.pd-prog__offered { display: flex; flex-direction: column; gap: 6px; margin: 10px 0 14px; }
.pd-prog__offered-label { font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--tu-ink-mute); }
.pd-prog__offered-row { display: flex; align-items: center; gap: 10px; min-width: 0; }
.pd-prog__school-copy { min-width: 0; }
.pd-prog__school-copy strong { display: block; font-size: 14.5px; line-height: 1.35; font-weight: 700; color: var(--tu-ink); }
.pd-prog__school-copy p { display: flex; align-items: center; gap: 4px; margin: 2px 0 0; font-size: 12.5px; color: var(--tu-ink-soft); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pd-prog__school-copy p .tu-ui-icon { display: inline-flex; flex: none; }
.pd-alloc { border: 1px solid var(--tu-line); border-radius: 14px; overflow: hidden; }
.pd-alloc__head { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; background: #181818; color: #fff; font-family: var(--tu-font-text); font-size: 13px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.pd-alloc__head b { font-family: var(--tu-font-display); font-size: 18px; letter-spacing: -.01em; text-transform: none; color: #4ade80; }
.pd-alloc__list { list-style: none; margin: 0; padding: 4px 0; max-height: 300px; overflow-y: auto; }
.pd-alloc__list li { display: grid; grid-template-columns: minmax(0, 1fr) 72px 70px; align-items: center; gap: 12px; padding: 9px 14px; border-bottom: 1px solid var(--tu-line-soft); }
.pd-alloc__list li:last-child { border-bottom: 0; }
.pd-alloc__name { min-width: 0; font-size: 14px; font-weight: 600; line-height: 1.35; color: var(--tu-ink); }
.pd-alloc__name small { display: block; margin-top: 2px; font-size: 12px; font-weight: 500; color: var(--tu-ink-mute); }
.pd-alloc__bar { height: 6px; border-radius: 999px; background: var(--tu-line); overflow: hidden; }
.pd-alloc__bar i { display: block; height: 100%; border-radius: 999px; background: #1c8a52; }
.pd-alloc__pts small { font-size: 11.5px; font-weight: 700; }
.pd-alloc__pts { white-space: nowrap; text-align: right; font-size: 14px; font-weight: 800; color: #1c8a52; }
@media (max-width: 540px) { .pd-alloc__list li { grid-template-columns: minmax(0, 1fr) 44px; } .pd-alloc__bar { display: none; } }
.pd-lock-total { margin: 12px 0 16px; font-size: 13px; font-weight: 600; color: var(--tu-ink-soft); }
.pd-lock-cta { width: 100%; justify-content: center; }
.pd-lock-login { display: block; margin-top: 14px; text-align: center; font-size: 13.5px; }
.pd-section-title { margin-bottom: 12px; line-height: 1.2; }
.pd-section-lead { margin: 0 0 24px; max-width: 680px; font-size: 16px; color: var(--tu-ink-soft); line-height: 1.6; }
.pd-requirements { margin-top: 24px; border: 1px solid var(--tu-card-border); border-radius: 14px; background: var(--tu-surface); }
.pd-requirements summary { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 22px 24px; cursor: pointer; list-style: none; }
.pd-requirements summary::-webkit-details-marker { display: none; }
.pd-requirements summary svg { flex: none; color: var(--tu-blue); }
.pd-requirements[open] summary svg { transform: rotate(180deg); }
.pd-requirements-title { display: block; font-family: var(--tu-font-display); font-weight: 700; font-size: 20px; color: var(--tu-ink); }
.pd-requirements-hint { display: block; font-size: 14px; color: var(--tu-ink-soft); margin-top: 6px; line-height: 1.5; }
.pd-requirements .pd-prose { margin: 0; max-width: 78ch; padding: 0 24px 24px; overflow-wrap: anywhere; }
.pd-prose ::v-deep img { max-width: 100%; height: auto; }
.pd-prose ::v-deep table { display: block; max-width: 100%; overflow-x: auto; }
.pd-program-filters { display: grid; grid-template-columns: minmax(0, 2fr) repeat(2, minmax(0, 1fr)); gap: 12px; }
.pd-program-results { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 56px; font-size: 13px; color: var(--tu-ink-soft); }
.pd-program-results p { margin: 0; }
.pd-program-results button { min-height: 44px; color: var(--tu-blue); font-weight: 600; }
.pd-no-results { font-size: 14px; color: var(--tu-ink-soft); padding: 20px 0; }
.pd-faq { max-width: 800px; margin-inline: auto; }
.pd-faq > h2 { text-align: center; margin-bottom: 24px; }
.pd-actions a:focus-visible, .pd-faq summary:focus-visible, .pd-requirements summary:focus-visible, .pd-program-results button:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 4px; border-radius: 8px; }
@media (max-width: 900px) { .pd-schools-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 720px) { .pd-program-filters { grid-template-columns: repeat(2, minmax(0, 1fr)); } .pd-program-filters input { grid-column: 1 / -1; } .pd-view-programs { justify-content: center; } }
@media (max-width: 540px) { .pd-schools-grid { grid-template-columns: 1fr; } .pd-requirements summary { padding: 18px; } .pd-requirements .pd-prose { padding: 0 18px 18px; } }
@media (prefers-reduced-motion: reduce) { .pd-actions a svg { transition: none !important; transform: none !important; } }
/* ---------- clean path page: centred hero, four steps, tinted panel ---------- */
.pd-hero .pd-path-meta { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-start; gap: 10px 18px; margin-top: 18px; }
.pd-hero .pd-plate-stat { display: inline-block; white-space: nowrap; font-size: 14px; color: var(--tu-ink-soft); }
.pd-hero .pd-lead { max-width: 620px; margin: 18px 0 0; font-size: 17px; line-height: 1.65; color: var(--tu-ink-soft); }
.pd-hero .pd-actions { justify-content: flex-start; margin-top: 24px; }
@media (max-width: 768px) { .pd-hero .pd-actions { flex-direction: row; align-items: center; } }

.pd-steps {
  margin-top: 64px;
  padding: 48px clamp(20px, 4vw, 48px) 40px;
  background: var(--tu-surface-2);
  border-radius: 24px;
}
.pd-steps .pd-section-title { text-align: center; margin: 0 0 32px; }
.pd-steps-list { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 18px; }
.pd-step {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  padding: 24px 22px 26px;
  border: 1px solid var(--tu-line-soft);
  border-radius: 14px;
  background: var(--tu-surface);
  box-shadow: 0 1px 2px rgba(24, 24, 24, .04);
}
.pd-step-num {
  width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center;
  border-radius: 50%; background: var(--tu-blue); color: #fff;
  font-family: var(--tu-font-display); font-weight: 700; font-size: 13.5px;
}
.pd-step-title { margin: 16px 0 0; font-size: 16px; line-height: 1.3; min-height: 2.6em; }
.pd-step-desc { margin: 8px 0 0; font-size: 14px; line-height: 1.6; color: var(--tu-ink-soft); }
.pd-steps-note { text-align: center; max-width: none; margin: 30px 0 0; font-size: 12.5px; color: var(--tu-ink-mute); }
.pd-faq { margin-top: 72px; }
.pd-similar { margin-top: 72px; }
.pd-similar .pd-section-title, .pd-schools .pd-section-title { text-align: center; }
.pd-similar .pd-section-lead, .pd-schools .pd-section-lead { margin-inline: auto; text-align: center; }

@media (max-width: 1024px) { .pd-steps-list { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 720px) {
  .pd-hero { padding-top: 12px; }
  .pd-steps { margin-top: 44px; padding: 32px 18px 28px; border-radius: 18px; }
  .pd-steps .pd-section-title { margin-bottom: 22px; }
  .pd-steps-list { grid-template-columns: 1fr; gap: 12px; }
  .pd-step { padding: 20px; }
  .pd-faq { margin-top: 48px; }
}
</style>

<style scoped>
/* A quieter hierarchy, using the existing Walter Neue / Diatype pairing. */
.tu-pathway-detail .pd-hero.pd-hero--band { grid-template-columns: minmax(0, 1fr) 420px; gap: 48px; }
@media (max-width: 1024px) { .tu-pathway-detail .pd-hero.pd-hero--band { grid-template-columns: minmax(0, 1fr) 320px; gap: 28px; } }
@media (max-width: 860px) { .tu-pathway-detail .pd-hero.pd-hero--band { grid-template-columns: 1fr; gap: 20px; } .pd-hero-photo { max-width: 520px; } }
.tu-pathway-detail .pd-hero.pd-hero--band .pd-title { font-size: clamp(34px, 4.4vw, 48px); max-width: 24ch; margin-bottom: 12px; }
.tu-pathway-detail .pd-hero.pd-hero--band .pd-lead { margin-top: 0; font-size: 18px; color: var(--tu-ink-soft); }
.tu-pathway-detail .pd-collab { grid-template-columns: 92px minmax(0, 1fr); padding: 16px 0 16px 20px; gap: 3px 12px; border: 0; border-left: 1px solid var(--tu-line-strong); border-radius: 0; background: transparent; }
.tu-pathway-detail .pd-collab__av { width: 92px; height: 92px; font-size: 26px; }
.tu-pathway-detail .pd-collab__name { font-family: var(--tu-font-display); font-weight: 700; font-size: 19px; letter-spacing: -0.01em; }
.tu-pathway-detail .pd-collab__label { color: var(--tu-ink-soft); }
.pd-overview-band { padding: 0; background: transparent; border: 0; border-radius: 0; display: grid; gap: 16px; }
.pd-overview-intro-wrap { padding: 0 0 2px; }
.pd-ov-block { padding: 24px 28px; background: var(--tu-surface); border: 1px solid var(--tu-line); border-radius: 16px; }
.tu-pathway-detail .pd-ov-title { display: flex; align-items: center; gap: 10px; margin: 0 0 4px; font-family: var(--tu-font-display) !important; font-weight: 700 !important; font-size: 20px; letter-spacing: -0.015em; color: var(--tu-ink); }
.pd-ov-count { display: inline-grid; place-items: center; min-width: 26px; height: 26px; padding: 0 8px; border-radius: 999px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); font-family: var(--tu-font-text); font-size: 12.5px; font-weight: 700; }
.pd-overview-intro { max-width: 70ch; margin: 0; font-size: 17px; line-height: 1.6; color: var(--tu-ink); }
.pd-helps { list-style: none; margin: 0; padding: 0 !important; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; }
.pd-helps li { display: grid; grid-template-columns: 30px minmax(0, 1fr); gap: 12px; align-items: start; min-width: 0; }
.pd-helps li + li { border-left: 1px solid var(--tu-line-strong); padding-left: 24px; }
.pd-helps__num { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%; background: var(--tu-cta); color: var(--tu-on-cta); font-family: var(--tu-font-display); font-weight: 700; font-size: 14px; }
.pd-helps b { display: block; font-size: 15.5px; font-weight: 700; color: var(--tu-ink); line-height: 1.3; margin-bottom: 4px; }
.pd-helps li > div > span { display: block; font-size: 14px; line-height: 1.55; color: var(--tu-ink-soft); }
@media (max-width: 760px) { .pd-helps { grid-template-columns: 1fr; gap: 16px; } .pd-helps li + li { border-left: 0; padding-left: 0; border-top: 1px solid var(--tu-line-strong); padding-top: 16px; } }
.tu-pathway-detail ol.pd-sequence--card { margin: 18px 0 0; gap: 16px; }
.tu-pathway-detail ol.pd-sequence--card > li, .tu-pathway-detail ol.pd-sequence--card > li:last-child { padding: 22px 22px 24px; border-radius: 16px; background: var(--tu-blue-tint); }
.pd-sequence--card h3 { margin: 16px 0 8px; }
.pd-sequence--card p { line-height: 1.6; }
.pd-sequence--card li:not(:last-child)::before { display: none; }
.pd-sequence--card .pd-sequence-num { width: 34px; height: 34px; font-size: 15px; }
.pd-sequence--card h3 { margin: 14px 0 6px; font-size: 16px; }
.pd-sequence--card p { font-size: 13.5px; }
.pd-overview-band .pd-incl { margin-top: 0; }
.pd-overview-facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; margin: 0; }
.pd-overview-facts > div + div { border-left: 1px solid var(--tu-line-strong); padding-left: 24px; }
.pd-overview-facts dt { margin-bottom: 6px; font-size: 13px; color: var(--tu-ink-soft); }
.pd-overview-facts dd { margin: 0; font-family: var(--tu-font-display); font-weight: var(--tu-fw-display, 800); font-size: 22px; line-height: 1.15; letter-spacing: -.01em; color: var(--tu-ink); }
.pd-overview-note { margin: 20px 0 0; font-size: 13px; line-height: 1.6; color: var(--tu-ink-soft); }
.pd-before { margin-top: 0; }
.pd-before__title { margin: 0 0 10px; font-size: 15px; font-weight: 700; color: var(--tu-ink); }
.pd-before__list { list-style: none; margin: 14px 0 0; padding: 0 !important; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 24px; }
.pd-before__list li { display: flex; align-items: flex-start; gap: 10px; font-size: 14.5px; line-height: 1.5; color: var(--tu-ink); }
.pd-before__list svg { flex: none; width: 20px; height: 20px; padding: 4px; margin-top: 1px; border-radius: 50%; background: var(--tu-blue-tint); color: var(--tu-blue-deep); box-sizing: border-box; }
.pd-before__note { margin: 12px 0 0; font-size: 14px; line-height: 1.55; color: var(--tu-ink-soft); }
@media (max-width: 760px) { .pd-before__list { grid-template-columns: 1fr; } }
.pd-fact-context summary { display: list-item; min-height: 32px; color: var(--tu-ink-soft); font-weight: 500; }
.pd-fact-context summary:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 4px; }
.pd-fact-context__body { margin-top: 16px; font-size: 14px; line-height: 1.6; color: var(--tu-ink-soft); }
.pd-more-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.pd-more-item { display: grid; grid-template-columns: 34px minmax(0, 1fr); gap: 12px; align-items: start; padding: 18px 20px; border: 1px solid var(--tu-line); border-radius: 14px; background: var(--tu-surface); }
.pd-more-item--pay { grid-column: 1 / -1; }
.pd-more-ico { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 10px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); }
.pd-more-ico .tu-ui-icon { display: inline-flex; }
.pd-more-item b { display: block; font-size: 14.5px; font-weight: 700; color: var(--tu-ink); margin-bottom: 4px; }
.pd-more-item p { margin: 0; font-size: 14px; line-height: 1.55; color: var(--tu-ink-soft); }
.pd-pay { display: flex; flex-wrap: wrap; gap: 10px 28px; margin: 12px 0 0; }
.pd-pay > div { display: flex; align-items: center; gap: 10px; }
.pd-pay dt, .pd-pay dd { margin: 0; }
.pd-pay-chip { font-size: 11px; padding: 4px 8px; }
.pd-pay dd { font-family: var(--tu-font-text); font-weight: 700; font-size: 17px; color: var(--tu-ink); }
.pd-pay dd small { font-size: 12.5px; font-weight: 500; color: var(--tu-ink-mute); margin-left: 1px; }
.pd-more-note { margin: 14px 0 0; font-size: 13px; color: var(--tu-ink-mute); }
@media (max-width: 760px) { .pd-more-grid { grid-template-columns: 1fr; } }
.tu-pathway-detail .pd-prog { border-color: var(--tu-line-strong); }
.tu-pathway-detail .pd-prog__copy h3, .tu-pathway-detail .pd-sim-row h3,
.tu-pathway-detail .pd-sequence h3, .tu-pathway-detail ::v-deep .tu-faq__q-text {
  font-family: var(--tu-font-text) !important; font-weight: 700 !important; letter-spacing: -0.005em; font-size: 17px; line-height: 1.4;
}
.tu-pathway-detail .pd-prog__copy p { color: var(--tu-ink-soft); }
.tu-pathway-detail .pd-panel.pd-steps { background: transparent; border-radius: 0; padding-left: 0; padding-right: 0; padding-bottom: 0; }
.pd-sequence { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0; list-style: none; padding: 0 !important; margin: 24px 0 0; }
.pd-sequence li { position: relative; padding-right: 28px; min-width: 0; }
.pd-sequence li:last-child { padding-right: 0; }
.pd-sequence li:not(:last-child)::before { content: ''; position: absolute; top: 21px; left: 42px; right: 0; height: 1px; background: var(--tu-line-strong); }
.pd-sequence-num { position: relative; display: grid; place-items: center; width: 42px; height: 42px; border-radius: 50%; color: var(--tu-on-cta); background: var(--tu-cta); font-family: var(--tu-font-display); font-weight: var(--tu-fw-display, 800); font-size: 19px; letter-spacing: -.01em; }
.pd-sequence h3 { margin: 18px 0 8px; }
.pd-sequence p { margin: 0; font-size: 14px; line-height: 1.65; color: var(--tu-ink-soft); }
.pd-sequence-note { margin: 26px 0 0; font-size: 13px; line-height: 1.6; color: var(--tu-ink-soft); }
.pd-incl { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 28px 48px; align-items: center; margin-top: 32px; padding: 44px 44px; border-radius: 20px; background: #181818; color: #fff; }
.pd-incl__kicker { display: inline-flex; align-items: center; height: 28px; padding: 0 12px; border-radius: 999px; background: var(--tu-blue); font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #fff; margin-bottom: 16px; }
.tu-pathway-detail .pd-incl__title { margin: 0 0 8px; font-family: var(--tu-font-display) !important; font-weight: 700 !important; font-size: 32px; line-height: 1.12; letter-spacing: -0.02em; color: #fff; }
.pd-incl__who { display: grid; grid-template-columns: 52px minmax(0, 1fr); gap: 14px; align-items: center; margin: 14px 0 22px; max-width: 46ch; }
.pd-incl__av { display: grid; place-items: center; width: 52px; height: 52px; border-radius: 50%; overflow: hidden; background: #fff; color: var(--tu-blue); font-size: 20px; font-weight: 700; box-shadow: 0 0 0 3px rgba(255,255,255,.14); }
.pd-incl__av img { width: 100%; height: 100%; object-fit: cover; }
.pd-incl__av img.pd-incl__av-brand { width: 46%; height: auto; object-fit: contain; }
.pd-incl__ask { display: block; margin: 0 0 10px; font-size: 14px; font-weight: 600; color: #fff; }
.pd-incl__who-text b { display: block; font-size: 15.5px; font-weight: 700; color: #fff; line-height: 1.3; }
.pd-incl__who-text span { display: block; margin-top: 2px; font-size: 14px; line-height: 1.5; color: rgba(255,255,255,.72); }
.pd-incl .pd-incl__btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 48px; padding: 0 22px; border-radius: var(--tu-r-md, 12px); font-family: var(--tu-font-text); font-weight: 700; font-size: 15px; text-decoration: none !important; background: var(--tu-blue) !important; color: #fff !important; border: 1.5px solid var(--tu-blue) !important; box-shadow: none; }
.pd-incl .pd-incl__btn:hover { background: var(--tu-blue-deep) !important; border-color: var(--tu-blue-deep) !important; }
.pd-incl .pd-incl__btn svg { width: 16px; height: 16px; }
.pd-incl__list { list-style: none; margin: 0; padding: 0 !important; display: grid; gap: 12px; }
.pd-incl__list li { display: grid; grid-template-columns: 52px minmax(0, 1fr); gap: 16px; align-items: center; padding: 18px 20px; border-radius: 18px; background: #fff; }
.pd-incl__ico { display: grid; place-items: center; width: 52px; height: 52px; border-radius: 14px; background: var(--tu-blue-tint); color: var(--tu-blue-deep) !important; }
.pd-incl__ico .tu-ui-icon, .pd-incl__ico svg { color: var(--tu-blue-deep) !important; fill: var(--tu-blue-deep); display: inline-flex; }
.pd-incl__list b { display: block; font-family: var(--tu-font-display); font-size: 16px; font-weight: 700; color: #181818; line-height: 1.3; }
.pd-incl__list span:not(.pd-incl__ico) { display: block; margin-top: 2px; font-size: 14px; line-height: 1.45; color: #5b5f66; }
@media (max-width: 760px) { .pd-incl { grid-template-columns: 1fr; padding: 24px 22px; border-radius: 16px; } .tu-pathway-detail .pd-incl__title { font-size: 26px; } .pd-incl__list li { padding: 16px; } }
@media (max-width: 760px) {
  .pd-bonus { grid-template-columns: 48px minmax(0, 1fr); padding: 20px; }
  .pd-bonus-avatar { align-self: start; }
  .pd-bonus-cta { grid-column: 1 / -1; width: 100%; justify-content: center; }
}
.tu-pathway-detail .pd-main ::v-deep .tu-faq { background: var(--tu-surface); border-color: var(--tu-line-strong); }
@media (max-width: 760px) {
  .tu-pathway-detail .pd-hero.pd-hero--band { grid-template-columns: 1fr; gap: 24px; }
  .tu-pathway-detail .pd-collab { border-left: 0; padding: 0; width: fit-content; }
  .pd-sequence { grid-template-columns: 1fr; gap: 0; }
  .pd-sequence li, .pd-sequence li:last-child { display: grid; grid-template-columns: 34px minmax(0, 1fr); gap: 16px; padding: 0 0 28px; }
  .tu-pathway-detail ol.pd-sequence--card > li, .tu-pathway-detail ol.pd-sequence--card > li:last-child { padding: 16px; gap: 12px; }
  .pd-sequence--card h3 { margin: 2px 0 6px; }
  .pd-sequence li:last-child { padding-bottom: 0; }
  .pd-sequence li:not(:last-child)::before { left: 16px; top: 34px; bottom: 0; width: 1px; height: auto; right: auto; }
  .pd-sequence h3 { margin: 3px 0 6px; }
}
@media (max-width: 540px) {
  .pd-ov-block { padding: 18px; border-radius: 14px; }
  .tu-pathway-detail ol.pd-sequence--card { gap: 10px; }
  .pd-overview-facts { grid-template-columns: 1fr; gap: 14px; }
  .pd-overview-facts > div + div { padding-left: 0; border-left: 0; border-top: 1px solid var(--tu-line-strong); padding-top: 14px; }
  .pd-overview-facts dd { font-size: 17px; }
}
/* More about this path: brand black, not the soft grey */
.tu-pathway-detail .pd-more-details > summary { display: inline-flex; align-items: center; gap: 10px; list-style: none; color: var(--tu-ink); font-weight: 700; }
.tu-pathway-detail .pd-more-details > summary::-webkit-details-marker { display: none; }
.tu-pathway-detail .pd-more-details > summary::marker { content: ''; }
.pd-more-details__plus { display: inline-grid; place-items: center; width: 26px; height: 26px; border-radius: 50%; background: var(--tu-blue-tint); color: var(--tu-blue-deep); flex: none; }
.pd-more-details__plus svg { width: 14px; height: 14px; }
.pd-more-details__plus .pd-plus-v { transition: transform .18s ease, opacity .18s ease; transform-origin: 12px 12px; }
.tu-pathway-detail .pd-more-details[open] > summary .pd-plus-v { transform: rotate(90deg); opacity: 0; }
.tu-pathway-detail .pd-fact-context__body, .tu-pathway-detail .pd-fact-context__body p, .tu-pathway-detail .pd-fact-context__body li,
.tu-pathway-detail .pd-prose, .tu-pathway-detail .pd-prose p, .tu-pathway-detail .pd-pay-list li { color: var(--tu-ink); }
.tu-pathway-detail .pd-fact-context__body strong { color: var(--tu-ink); font-weight: 700; }
.pd-overview-intro-wrap .pd-overview-intro + .pd-overview-intro { margin-top: 10px; }
.pd-view-more { display: inline-flex; align-items: center; gap: 4px; margin-top: 6px; padding: 0; border: 0; background: none; color: var(--tu-blue); font-family: var(--tu-font-text); font-size: 15px; font-weight: 600; line-height: 1.4; cursor: pointer; }
.pd-view-more::after { content: ''; width: 7px; height: 7px; border-right: 1.8px solid currentColor; border-bottom: 1.8px solid currentColor; transform: rotate(45deg) translateY(-2px); margin-left: 2px; }
.pd-view-more:hover { text-decoration: underline; text-underline-offset: 3px; }

/* ---- 2026-10-21 layout pass (mockup): same colors, new arrangement ---- */
/* hero: route above the title, facts in a tinted panel, a quiet browse link */
.tu-pathway-detail .pd-hero-route { margin: 0 0 14px; }
.tu-pathway-detail .pd-title { margin-bottom: 14px; }
.tu-pathway-detail .pd-hero-facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(96px, 1fr)); gap: 12px 24px; max-width: 460px; margin: 22px 0 0; padding: 16px 20px; border-radius: 14px; background: var(--tu-blue-tint); }
.tu-pathway-detail .pd-hero-facts dt { font-size: 12px; font-weight: 500; letter-spacing: 0; color: var(--tu-ink-soft); }
.tu-pathway-detail .pd-hero-facts dd { margin-top: 4px; font-size: 19px; }
.tu-pathway-detail .pd-band-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 22px; }
.pd-browse-link { display: inline-flex; align-items: center; gap: 6px; font-size: 15px; font-weight: 600; color: var(--tu-ink-soft) !important; text-decoration: none !important; }
.pd-browse-link svg { width: 16px; height: 16px; }
.pd-browse-link:hover { color: var(--tu-blue) !important; }
@media (max-width: 760px) { .tu-pathway-detail .pd-hero-facts { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px 14px; margin-inline: auto; padding: 14px 16px; } .tu-pathway-detail .pd-hero-facts dd { font-size: 16px; } .tu-pathway-detail .pd-band-actions { display: none; } }

/* overview: copy + how it works on the left, requirements as a side card */
.pd-ov-grid { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(280px, 1fr); gap: 40px; align-items: start; }
.pd-ov-grid--solo { grid-template-columns: minmax(0, 1fr); }
.pd-ov-main { min-width: 0; }
.pd-ov-main .pd-overview-intro { font-size: 16px; color: var(--tu-ink-soft); margin-top: 14px; }
.pd-ov-how { margin-top: 30px; }
.tu-pathway-detail .pd-ov-how .pd-ov-title { font-size: 19px; margin-bottom: 0; }
.tu-pathway-detail ol.pd-how-list { list-style: none; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 26px 32px; margin: 20px 0 0; padding: 0; }
.pd-how-list li { display: grid; grid-template-columns: 28px minmax(0, 1fr); gap: 12px; align-items: start; }
.pd-how-num { display: grid; place-items: center; width: 28px; height: 28px; border-radius: 50%; background: var(--tu-blue-tint); color: var(--tu-blue-deep); font-size: 13px; font-weight: 700; }
.tu-pathway-detail .pd-how-list h4 { margin: 3px 0 6px; font-family: var(--tu-font-text); font-size: 15.5px; font-weight: 700; color: var(--tu-ink); }
.pd-how-list p { margin: 0; font-size: 14px; line-height: 1.6; color: var(--tu-ink-soft); }
.pd-ov-side { padding: 24px 24px 26px; border-radius: 16px; background: var(--tu-surface-2, #f3f5f8); }
.tu-pathway-detail .pd-ov-side .pd-ov-title { font-size: 19px; }
.tu-pathway-detail .pd-ov-side .pd-before__list { grid-template-columns: 1fr; gap: 12px; margin-top: 16px; }
.tu-pathway-detail .pd-ov-side .pd-before__list svg { background: transparent; padding: 2px; color: var(--tu-blue); }
.tu-pathway-detail .pd-member .pd-incl { margin-top: 0; }
@media (max-width: 900px) { .pd-ov-grid { grid-template-columns: minmax(0, 1fr); gap: 28px; } }
@media (max-width: 600px) { .tu-pathway-detail ol.pd-how-list { grid-template-columns: 1fr; gap: 18px; } }

/* Enrolled tracker: boxless rows, an open circle per step joined by a thin rail */
.tu-pathway-detail ul.pd-track { list-style: none; margin: 20px 0 0; padding: 0; display: flex; flex-direction: column; }
.pd-track__item { position: relative; display: grid; grid-template-columns: 26px minmax(0, 1fr); gap: 14px; align-items: start; padding: 0 0 24px; }
.pd-track__item::before { content: ''; position: absolute; left: 12px; top: 30px; bottom: 2px; width: 2px; border-radius: 2px; background: var(--tu-line); }
.pd-track__item:last-child { padding-bottom: 0; }
.pd-track__item:last-child::before { display: none; }
.pd-track__box { display: block; width: 26px; height: 26px; margin-top: 1px; border-radius: 50%; border: 2px solid var(--tu-line-strong); background: var(--tu-surface); box-sizing: border-box; }
.tu-pathway-detail .pd-track h4 { margin: 3px 0 6px; font-family: var(--tu-font-text); font-size: 15.5px; font-weight: 700; color: var(--tu-ink); }
.pd-track p { margin: 0; font-size: 14px; line-height: 1.6; color: var(--tu-ink-soft); }

/* programs: eyebrow header, search + state, school-first cards */
.pd-prog-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; }
.pd-prog-head > div { min-width: 0; max-width: 640px; }
.pd-eyebrow { display: block; margin-bottom: 8px; font-size: 12px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--tu-blue); }
.pd-prog-head__link { flex: none; margin-bottom: 6px; font-size: 14px; font-weight: 600; color: var(--tu-ink-soft) !important; text-decoration: none !important; }
.pd-prog-head__link:hover { color: var(--tu-blue) !important; }
.pd-prog-filters { display: grid; grid-template-columns: minmax(0, 1fr) 220px 280px; gap: 12px; margin: 22px 0 18px; }
@media (max-width: 960px) { .pd-prog-filters { grid-template-columns: 1fr 1fr; } .pd-prog-search { grid-column: 1 / -1; } }
.pd-prog-search, .pd-prog-state { position: relative; display: flex; align-items: center; gap: 8px; height: 48px; padding: 0 14px; border: 1px solid transparent; border-radius: 12px; background: var(--tu-surface-2, #f3f5f8); color: var(--tu-ink-mute); box-sizing: border-box; transition: border-color .15s ease, background .15s ease; }
.pd-prog-search:hover, .pd-prog-state:hover { border-color: var(--tu-line); }
.pd-prog-search:focus-within, .pd-prog-state:focus-within { border-color: var(--tu-line-strong); background: var(--tu-surface); box-shadow: none; }
/* The site's global form focus styles draw a second blue box inside the field; the field itself shows focus. */
.pd-prog-search input, .pd-prog-search input:focus, .pd-prog-state select, .pd-prog-state select:focus { outline: none !important; box-shadow: none !important; border: 0 !important; }
.pd-prog-search .tu-ui-icon { display: inline-flex; flex: none; }
.pd-prog-search input { flex: 1; min-width: 0; height: 46px; border: 0; outline: 0; background: transparent; font: 500 15px var(--tu-font-text); color: var(--tu-ink); }
.pd-prog-search input::placeholder { color: var(--tu-ink-mute); }
.pd-prog-state > span { flex: none; font-size: 13px; font-weight: 600; color: var(--tu-ink-mute); }
.pd-prog-state select { flex: 1; min-width: 0; height: 46px; border: 0; outline: 0; background: transparent; appearance: none; -webkit-appearance: none; font: 700 14.5px var(--tu-font-text); color: var(--tu-ink); cursor: pointer; padding-right: 24px; text-overflow: ellipsis; }
.pd-prog-state svg { position: absolute; right: 12px; width: 16px; height: 16px; color: var(--tu-ink-soft); pointer-events: none; }
.pd-prog-none { margin: 4px 0 18px; font-size: 15px; color: var(--tu-ink-soft); }
@media (max-width: 640px) { .pd-prog-head { flex-direction: column; align-items: flex-start; gap: 6px; } .pd-prog-filters { grid-template-columns: 1fr; } }

.tu-pathway-detail .pd-prog { gap: 0; padding: 20px; border-radius: 14px; }
.pd-prog__head { margin-bottom: 14px; }
.pd-prog__school { display: flex; align-items: center; gap: 10px; min-width: 0; }
.pd-prog__school .pd-prog__logo { flex: 0 0 32px; width: 32px; height: 32px; border-radius: 8px; }
.tu-pathway-detail .pd-prog__title { margin: 0; font-size: 17px; line-height: 1.3; font-weight: 700; color: var(--tu-ink); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.pd-prog__loc { display: flex; align-items: center; gap: 5px; margin: 12px 0 0; font-size: 13px; color: var(--tu-ink-soft); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pd-prog__loc .tu-ui-icon { display: inline-flex; flex: none; color: var(--tu-ink-mute); }
.tu-pathway-detail .pd-prog__program { margin: 10px 0 16px; font-size: 14px; font-weight: 500; color: var(--tu-ink-soft); }
.pd-prog__rows { margin: auto 0 0; padding: 14px 0 0; border-top: 1px solid var(--tu-line-soft); display: grid; gap: 10px; }
.pd-prog__rows > div { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.pd-prog__rows dt { margin: 0; font-size: 13px; color: var(--tu-ink-soft); }
.pd-prog__rows dd { margin: 0; font-size: 13.5px; font-weight: 700; color: var(--tu-ink); text-align: right; }
.pd-prog__cta { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: auto; padding: 11px 14px; border-radius: 10px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); font-size: 14px; font-weight: 700; }
.pd-prog__rows + .pd-prog__cta { margin-top: 18px; }
.tu-pathway-detail .pd-schools-grid { align-items: stretch; }
.tu-pathway-detail .pd-prog { height: 100%; box-sizing: border-box; }
.pd-prog__cta svg { width: 16px; height: 16px; }
.pd-prog--locked:hover .pd-prog__cta { background: var(--tu-blue); color: #fff; }

/* prerequisites section */
.pd-prq-grid { list-style: none; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin: 0; padding: 0 !important; }
.pd-prq-item { display: flex; align-items: center; gap: 12px; width: 100%; box-sizing: border-box; min-height: 60px; padding: 10px 14px; border: 1px solid var(--tu-line); border-radius: 12px; background: var(--tu-surface); text-align: left; font: inherit; color: inherit; cursor: pointer; transition: border-color .15s ease, background .15s ease; }
.pd-prq-item:hover { border-color: var(--tu-blue); background: var(--tu-blue-tint); }
.pd-prq-item:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 2px; }
.pd-prq-item__ico { display: grid; place-items: center; flex: none; width: 36px; height: 36px; border-radius: 10px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); }
.pd-prq-item__ico .tu-ui-icon { display: inline-flex; }
.pd-prq-item__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.pd-prq-item__text b { font-size: 14.5px; font-weight: 700; line-height: 1.3; color: var(--tu-ink); }
.pd-prq-item__text small { font-size: 12.5px; color: var(--tu-ink-soft); }
.pd-prq-item__go { width: 16px; height: 16px; flex: none; color: var(--tu-ink-mute); }
@media (max-width: 960px) { .pd-prq-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 560px) { .pd-prq-grid { grid-template-columns: 1fr; } }
.tu-prq-group { margin-top: 10px; border: 1px solid var(--tu-line); border-radius: 16px; background: var(--tu-surface); padding: 0 16px; transition: border-color .15s ease; }
.tu-prq-group:hover, .tu-prq-group[open] { border-color: var(--tu-line-strong); }
.tu-prq-group__ico { display: grid; place-items: center; flex: none; width: 40px; height: 40px; border-radius: 12px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); }
.tu-prq-group__ico .tu-ui-icon { display: inline-flex; }
.pd-prq-head { display: flex; align-items: center; gap: 10px; }
.tu-prq-group:first-of-type { margin-top: 14px; }
.tu-prq-group__sum { display: flex; align-items: center; gap: 12px; padding: 14px 0; list-style: none; cursor: pointer; }
.tu-prq-group__sum::-webkit-details-marker { display: none; }
.tu-prq-group__sum:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 2px; border-radius: 8px; }
.tu-prq-group__title { margin: 0 !important; font-family: var(--tu-font-text) !important; font-size: 16px !important; font-weight: 700 !important; color: var(--tu-ink); }
.tu-prq-group__count { display: inline-grid; place-items: center; min-width: 26px; height: 24px; padding: 0 8px; border-radius: 999px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); font-size: 12.5px; font-weight: 700; }
.tu-prq-group__chev { width: 18px; height: 18px; margin-left: auto; color: var(--tu-ink-mute); transition: transform .15s ease; }
.tu-prq-group[open] .tu-prq-group__chev { transform: rotate(180deg); }
.tu-prq-group[open] > ul { padding-bottom: 16px !important; }
.pd-incl__btn-ico { display: inline-flex; flex: none; }
.pd-incl__btn-ico .tu-ui-icon { display: inline-flex !important; }
</style>

<style lang="scss" scoped>
/* Portal only: the enrolled program cards (scorecards) and the Enrolled pill. */
/* The route chips are an inline box, so the pill is block level to drop onto its own line. */
.pd-enrolled-pill { display: flex; width: max-content; align-items: center; gap: 6px; margin: 14px 0 12px; padding: 6px 12px; border-radius: 999px; background: var(--tu-green-tint); color: var(--tu-green); font-size: 12px; font-weight: 700; letter-spacing: 0.02em; }
.pd-prog--enrolled { cursor: default; }
.pd-ring { flex: none; width: 48px; height: 48px; border-radius: 50%; display: grid; place-items: center; }
.pd-ring__inner { width: 38px; height: 38px; border-radius: 50%; background: var(--tu-surface, #fff); display: grid; place-items: center; font-family: var(--tu-font-display); font-weight: var(--tu-fw-display); font-size: 11px; letter-spacing: -0.01em; color: var(--tu-ink); font-variant-numeric: tabular-nums; }
.pd-prog__bar { position: relative; height: 8px; margin-top: 12px; border-radius: 999px; background: var(--tu-surface-2); overflow: hidden; }
.pd-prog__bar span { display: block; height: 100%; border-radius: 999px; background: var(--tu-blue); transition: width 0.4s ease; }
.pd-prog--enrolled .pd-prog__cta { margin-top: 12px; }
/* Academic check (the audit, coming soon) sits where Browse programs was: a muted bolt link. */
.tu-scope .pd-check-link { display: inline-flex; align-items: center; gap: 8px; border: 0; background: transparent; font: inherit; color: var(--tu-ink-mute); cursor: not-allowed; opacity: 0.7; }
.tu-scope .pd-check-link svg { width: 18px; height: 18px; color: var(--tu-blue); }
/* Apply now on every program card: disabled until applications open, live on the student's own programs. */
.tu-scope .pd-prog__cta--apply { justify-content: center; width: 100%; min-height: 44px; margin-top: auto; padding: 0 16px; border-radius: 10px; background: var(--tu-line-soft); color: var(--tu-ink-mute); font-weight: 700; cursor: not-allowed; text-decoration: none; }
.tu-scope .pd-prog__cta--apply.is-live { background: var(--tu-ink); color: #fff !important; cursor: pointer; }
.tu-scope .pd-prog__cta--apply.is-live:hover { background: var(--tu-blue); }
/* The portal's global heading and paragraph colors win over the band's white text; pin it here. */
.tu-scope .pd-member .pd-incl__title, .tu-scope .pd-member .pd-incl__who-text b { color: #fff !important; }
.tu-scope .pd-member .pd-incl__who-text span { color: rgba(255, 255, 255, 0.72) !important; }
</style>
